import React, { useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Download, Share2, Check, CaseSensitive, Pipette, AlignLeft, AlignCenter, AlignRight, Palette, Type, Type as FontIcon, Maximize2, Sparkles, Layout, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Move, Plus, Minus, RotateCw, FlipHorizontal, Trash2, Pencil, RotateCcw, Circle, Layers, Eye, Baseline } from 'lucide-react';
import { toJpeg } from 'html-to-image';
import { useTheme } from '../contexts/ThemeContext';
import { socialService } from '../services/socialService';
import { useAuth } from '../contexts/AuthContext';
import { importRateLimiter } from '../utils/importRateLimit';
import SocialShareModal from './SocialShareModal';
import { useTranslation } from 'react-i18next';
import { getQuoteCardText } from './quoteCardI18n';
import ColorWheel from './ColorWheel';
import i18nInstance from '../i18n';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ShareQuoteData {
    text: string;
    bookTitle: string;
    authorName: string;
    coverUrl: string | null;
    bookId?: string;
}

interface Props {
    data: ShareQuoteData;
    onClose: () => void;
}

type Template = 'minimalista' | 'papirus' | 'glass' | 'foto-real-1' | 'foto-real-2' | 'foto-real-3' | 'foto-real-4' | 'foto-real-5' | 'foto-real-6' | 'foto-real-7' | 'collage-h2' | 'collage-v2' | 'collage-3' | 'collage-4';

const FOTO_REAL_1_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/bookpages-template/template-1.png";
const FOTO_REAL_2_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/bookpages-template/template-2.png";
const FOTO_REAL_3_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/bookpages-template/template-3.png";
const FOTO_REAL_4_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/bookpages-template/template-4.png";
const FOTO_REAL_5_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/bookpages-template/template-5.png";
const FOTO_REAL_6_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/bookpages-template/template-6.png";
const FOTO_REAL_7_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/bookpages-template/template-7.png";

const FOTO_REAL_URLS: Record<string, string> = {
    'foto-real-1': FOTO_REAL_1_URL,
    'foto-real-2': FOTO_REAL_2_URL,
    'foto-real-3': FOTO_REAL_3_URL,
    'foto-real-4': FOTO_REAL_4_URL,
    'foto-real-5': FOTO_REAL_5_URL,
    'foto-real-6': FOTO_REAL_6_URL,
    'foto-real-7': FOTO_REAL_7_URL,
};

export interface FotoRealTransform {
    left: number; // in %
    top: number; // in %
    width: number; // in px
    rotation: number; // in deg
    fontScale: number; // multiplier
    warpScale: number; // distortion magnitude
    skewX: number; // deg
    skewY: number; // deg
    freqX: number;
    freqY: number;
    p1: { x: number; y: number };
    p2: { x: number; y: number };
    p3: { x: number; y: number };
    p4: { x: number; y: number };
    textAlign?: 'left' | 'center' | 'right';
    opacity?: number;
}

const DEFAULT_FOTO_REAL_TRANSFORMS: Record<string, FotoRealTransform> = {
    'foto-real-1': { left: 40.9, top: 22.8, width: 220, rotation: -28, fontScale: 1.0, warpScale: 3.0, skewX: 0, skewY: 0, freqX: 0.002, freqY: 0.008, p1: { x: 0, y: 0 }, p2: { x: 0, y: 0 }, p3: { x: 0, y: 0 }, p4: { x: 0, y: 0 }, opacity: 0.85 },
    'foto-real-2': { left: 18, top: 32, width: 360, rotation: -28, fontScale: 1.0, warpScale: 3.0, skewX: 0, skewY: 0, freqX: 0.003, freqY: 0.015, p1: { x: 0, y: 0 }, p2: { x: 0, y: 0 }, p3: { x: 0, y: 0 }, p4: { x: 0, y: 0 }, textAlign: 'center', opacity: 0.85 },
    'foto-real-3': { left: 24, top: 28, width: 340, rotation: 12, fontScale: 1.0, warpScale: 2.5, skewX: 0, skewY: 0, freqX: 0.003, freqY: 0.01, p1: { x: 0, y: 0 }, p2: { x: 0, y: 0 }, p3: { x: 0, y: 0 }, p4: { x: 0, y: 0 }, opacity: 0.85 },
    'foto-real-4': { left: 28, top: 30, width: 340, rotation: -8, fontScale: 1.0, warpScale: 3.0, skewX: 0, skewY: 0, freqX: 0.002, freqY: 0.012, p1: { x: 0, y: 0 }, p2: { x: 0, y: 0 }, p3: { x: 0, y: 0 }, p4: { x: 0, y: 0 }, opacity: 0.85 },
    'foto-real-5': { left: 22, top: 34, width: 340, rotation: 18, fontScale: 1.0, warpScale: 3.0, skewX: 0, skewY: 0, freqX: 0.003, freqY: 0.014, p1: { x: 0, y: 0 }, p2: { x: 0, y: 0 }, p3: { x: 0, y: 0 }, p4: { x: 0, y: 0 }, opacity: 0.85 },
    'foto-real-6': { left: 30, top: 26, width: 340, rotation: -14, fontScale: 1.0, warpScale: 2.5, skewX: 0, skewY: 0, freqX: 0.002, freqY: 0.01, p1: { x: 0, y: 0 }, p2: { x: 0, y: 0 }, p3: { x: 0, y: 0 }, p4: { x: 0, y: 0 }, opacity: 0.85 },
    'foto-real-7': { left: 25, top: 30, width: 340, rotation: 5, fontScale: 1.0, warpScale: 3.0, skewX: 0, skewY: 0, freqX: 0.003, freqY: 0.012, p1: { x: 0, y: 0 }, p2: { x: 0, y: 0 }, p3: { x: 0, y: 0 }, p4: { x: 0, y: 0 }, opacity: 0.85 },
};

function getDefaultFotoRealTransform(templateId: string): FotoRealTransform {
    return DEFAULT_FOTO_REAL_TRANSFORMS[templateId] || {
        left: 30, top: 30, width: 340, rotation: 0, fontScale: 1.0, warpScale: 3.0, skewX: 0, skewY: 0, freqX: 0.003, freqY: 0.012,
        p1: { x: 0, y: 0 }, p2: { x: 0, y: 0 }, p3: { x: 0, y: 0 }, p4: { x: 0, y: 0 }, opacity: 0.85
    };
}

// ─── Card base dimensions (export at ×2 = 1080×1920) ────────────────────────

const CARD_W = 540;
const CARD_H = 960;

// ─── Editor options ──────────────────────────────────────────────────────────

const FONT_SIZES = [
    { id: 'sm', label: 'P', px: 22 },
    { id: 'md', label: 'M', px: 28 },
    { id: 'lg', label: 'G', px: 36 },
    { id: 'xl', label: 'XG', px: 46 },
];

const BG_COLORS = [
    { id: 'bk', label: 'Grafite', value: '#161615' },
    { id: 'dp', label: 'Roxo', value: '#1a0533' },
    { id: 'nb', label: 'Navy', value: '#0f172a' },
    { id: 'fe', label: 'Floresta', value: '#0f2417' },
    { id: 'ro', label: 'Vinho', value: '#3b0a2a' },
    { id: 'am', label: 'Âmbar', value: '#1c1205' },
    { id: 'te', label: 'Água', value: '#041c1c' },
    { id: 'sl', label: 'Ardósia', value: '#1e293b' },
    { id: 'pg', label: 'Pergaminho', value: '#f5f0e8' },
    { id: 'cr', label: 'Creme', value: '#faf6f0' },
];

const FONTS = [
    { id: 'inter', label: 'Inter', family: 'Inter, sans-serif', gQuery: 'Inter:wght@400;700;900' },
    { id: 'playfair', label: 'Playfair', family: "'Playfair Display', serif", gQuery: 'Playfair+Display:ital,wght@0,700;1,400' },
    { id: 'merriweather', label: 'Merriweather', family: "'Merriweather', serif", gQuery: 'Merriweather:ital,wght@0,700;1,400' },
    { id: 'lora', label: 'Lora', family: "'Lora', serif", gQuery: 'Lora:ital,wght@0,700;1,400' },
    { id: 'montserrat', label: 'Montserrat', family: "'Montserrat', sans-serif", gQuery: 'Montserrat:wght@400;700;900' },
    { id: 'raleway', label: 'Raleway', family: "'Raleway', sans-serif", gQuery: 'Raleway:wght@400;700;900' },
    { id: 'oswald', label: 'Oswald', family: "'Oswald', sans-serif", gQuery: 'Oswald:wght@400;700' },
    { id: 'georgia', label: 'Georgia', family: 'Georgia, serif', gQuery: null },
    { id: 'crimson', label: 'Crimson', family: "'Crimson Text', serif", gQuery: 'Crimson+Text:ital,wght@0,600;1,400' },
    { id: 'garamond', label: 'EB Garamond', family: "'EB Garamond', serif", gQuery: 'EB+Garamond:ital,wght@0,500;1,400' },
    // ── Caligrafia
    { id: 'dancing', label: 'Dancing', family: "'Dancing Script', cursive", gQuery: 'Dancing+Script:wght@400;700' },
    { id: 'pacifico', label: 'Pacifico', family: "'Pacifico', cursive", gQuery: 'Pacifico' },
    { id: 'greatvibes', label: 'Great Vibes', family: "'Great Vibes', cursive", gQuery: 'Great+Vibes' },
    // ── Futuristas
    { id: 'orbitron', label: 'Orbitron', family: "'Orbitron', sans-serif", gQuery: 'Orbitron:wght@400;700;900' },
    { id: 'rajdhani', label: 'Rajdhani', family: "'Rajdhani', sans-serif", gQuery: 'Rajdhani:wght@400;700' },
    // ── Retrô
    { id: 'elite', label: 'Typewriter', family: "'Special Elite', cursive", gQuery: 'Special+Elite' },
];

const TEMPLATES: { id: Template; label: string }[] = [
    { id: 'minimalista', label: 'Minimal' },
    { id: 'papirus', label: 'Papirus' },
    { id: 'glass', label: 'Glass' },
    { id: 'foto-real-1', label: 'Foto Real 1' },
    { id: 'foto-real-2', label: 'Foto Real 2' },
    { id: 'foto-real-3', label: 'Foto Real 3' },
    { id: 'foto-real-4', label: 'Foto Real 4' },
    { id: 'foto-real-5', label: 'Foto Real 5' },
    { id: 'foto-real-6', label: 'Foto Real 6' },
    { id: 'foto-real-7', label: 'Foto Real 7' },
    { id: 'collage-h2', label: 'Horizontal 2' },
    { id: 'collage-v2', label: 'Vertical 2' },
    { id: 'collage-3', label: 'Grid 3' },
    { id: 'collage-4', label: 'Grid 4' },
];

// ─── Dot texture SVG (minimalista) ───────────────────────────────────────────
const DOT_TEXTURE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Ccircle cx='24' cy='24' r='1.2' fill='%23ffffff' fill-opacity='0.18'/%3E%3C/svg%3E")`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isLight(hex: string): boolean {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 128;
}

// Fetch Google Font CSS → replace font URLs with base64 data URIs
const fontCache: Record<string, string> = {};
async function fetchEmbeddedFontCSS(gQuery: string): Promise<string> {
    if (fontCache[gQuery]) return fontCache[gQuery];
    try {
        const url = `https://fonts.googleapis.com/css2?family=${gQuery}&display=swap`;
        const css = await fetch(url).then(r => r.text());

        const urlMatches = [...css.matchAll(/url\((https?:\/\/[^)'"]+)\)/g)];
        let embedded = css;

        for (const match of urlMatches) {
            const fontUrl = match[1];
            try {
                const buf = await fetch(fontUrl).then(r => r.arrayBuffer());
                const bytes = new Uint8Array(buf);
                let binary = '';
                const chunkSize = 8192;
                for (let i = 0; i < bytes.length; i += chunkSize) {
                    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
                }
                const b64 = btoa(binary);
                const mime = fontUrl.includes('.woff2') ? 'font/woff2' : 'font/woff';
                embedded = embedded.replace(fontUrl, `data:${mime};base64,${b64}`);
            } catch { /* skip individual font variant on error */ }
        }

        fontCache[gQuery] = embedded;
        return embedded;
    } catch {
        return '';
    }
}

function hexToRgba(hex: string, alpha: number): string {
    const n = parseInt(hex.replace('#', ''), 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

// Cores rápidas do botão "Cor do texto".
const TEXT_COLORS = ['#ffffff', '#1a1a1a', '#23190f', '#E8823F', '#d4a72c', '#b23a48', '#3a6ea5', '#2f7d5b', '#7c5cbf', '#e88aa8'];

// Pixels de deslocamento vertical por unidade de "curva" (slider vai de -15 a 15).
const CURVE_PX = 6;

const TEXT_HIGHLIGHT: React.CSSProperties = {
    backgroundColor: 'rgba(254, 240, 138, 0.75)',
    boxShadow: '0 0 0 3px rgba(254, 240, 138, 0.75)',
    borderRadius: '2px',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
};

/**
 * Texto curvado em arco, letra a letra: cada caractere sobe/desce numa parábola e gira
 * na tangente. Fica nítido (é texto de verdade) e sai igual no html-to-image, ao contrário
 * do antigo feDisplacementMap, que pixelizava e "picotava" as letras.
 * curve > 0 = arco para cima, curve < 0 = arco para baixo.
 */
const CurvedText: React.FC<{ text: string; curve: number; highlight?: boolean }> = ({ text, curve, highlight }) => {
    const rootRef = useRef<HTMLSpanElement>(null);

    const layout = useCallback(() => {
        const root = rootRef.current;
        // Mede pela caixa de texto inteira (offsetParent), para título e citação seguirem o mesmo arco.
        const box = root?.offsetParent as HTMLElement | null;
        if (!root || !box || !curve) return;
        const half = box.clientWidth / 2;
        if (!half) return;
        const amp = curve * CURVE_PX;
        // offsetLeft ignora transforms (rotação/escala do cartão), então a medida é estável.
        root.querySelectorAll<HTMLSpanElement>('[data-ch]').forEach(c => {
            const x = c.offsetLeft + c.offsetWidth / 2;
            const t = Math.max(-1, Math.min(1, (x - half) / half));
            const dy = -amp * (1 - t * t);
            const rot = (Math.atan((2 * amp * t) / half) * 180) / Math.PI;
            c.style.transform = `translateY(${dy.toFixed(2)}px) rotate(${rot.toFixed(2)}deg)`;
        });
    }, [curve]);

    // Recalcula a cada render (fonte, tamanho, largura e alinhamento mudam as posições).
    useLayoutEffect(() => { layout(); });

    useEffect(() => {
        const box = rootRef.current?.parentElement;
        if (!box) return;
        const ro = new ResizeObserver(() => layout());
        ro.observe(box);
        document.fonts?.ready.then(() => layout()).catch(() => {});
        return () => ro.disconnect();
    }, [layout]);

    if (!curve) return <span style={highlight ? TEXT_HIGHLIGHT : undefined}>{text}</span>;

    const charStyle: React.CSSProperties = {
        display: 'inline-block',
        transformOrigin: '50% 75%',
        textDecoration: 'inherit',
        ...(highlight ? TEXT_HIGHLIGHT : {}),
    };

    return (
        <span ref={rootRef} role="img" aria-label={text}>
            {text.split(/(\s+)/).map((part, i) =>
                !part || /^\s+$/.test(part) ? part : (
                    <span key={i} aria-hidden="true" style={{ whiteSpace: 'nowrap' }}>
                        {Array.from(part).map((ch, j) => <span key={j} data-ch="" style={charStyle}>{ch}</span>)}
                    </span>
                )
            )}
        </span>
    );
};

// ─── Card Component (all inline styles — html-to-image safe) ─────────────────

interface ImageTransform {
    scale: number;
    x: number;
    y: number;
    rotate: number;
    flipX: boolean;
}

interface CardProps {
    data: ShareQuoteData;
    template: Template;
    bgColor: string;
    fontFamily: string;
    fontSize: number; // in px for CARD_W=540 base
    isBold?: boolean;
    isItalic?: boolean;
    isUnderline?: boolean;
    textAlign?: 'left' | 'center' | 'right';
    /** Cor do texto escolhida pelo usuário; null = automática (contraste com o fundo). */
    textColorOverride?: string | null;
    images?: Record<number, string>;
    transforms?: Record<number, ImageTransform>;
    fotoRealTransform?: FotoRealTransform;
    isTextSelected?: boolean;
    onSlotClick?: (index: number) => void;
    onTextClick?: () => void;
    onTextPointerDown?: (handle: 'move' | 'rotate' | 'resize' | 'p1' | 'p2' | 'p3' | 'p4', e: React.PointerEvent) => void;
}

const SlotWrapper: React.FC<{
    children: React.ReactNode;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties
}> = ({ children, onClick, className = '', style = {} }) => (
    <div
        onClick={onClick}
        className={`relative overflow-hidden cursor-pointer group ${className}`}
        style={{
            flex: 1,
            margin: 0,
            borderRadius: 0,
            backgroundColor: 'rgba(0,0,0,0.1)',
            ...style
        }}
    >
        {children}
    </div>
);

const EmptySlot: React.FC<{ label: string }> = ({ label }) => (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-white/40 group-hover:bg-white/5 transition-all no-export">
        <Sparkles size={24} className="mb-2 opacity-50" />
        <span className="text-[10px] uppercase font-mono font-normal tracking-[0.14em]">{label}</span>
    </div>
);

const renderImage = (src: string, transform?: ImageTransform) => {
    const { scale = 1, x = 0, y = 0, rotate = 0, flipX = false } = transform || {};
    return (
        <img
            src={src}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                // Panning via object-position keeps the image element boundaries fixed
                objectPosition: `calc(50% + ${x}px) calc(50% + ${y}px)`,
                transform: `rotate(${rotate}deg) scaleX(${flipX ? -1 : 1}) scale(${scale})`,
                transition: 'transform 0.1s linear, object-position 0.1s linear',
                transformOrigin: 'center center'
            }}
            crossOrigin="anonymous"
            alt="Slot"
        />
    );
};

const QuoteCard = React.forwardRef<HTMLDivElement, CardProps>(
    ({ data, template, bgColor, fontFamily, fontSize, isBold, isItalic, isUnderline, textAlign = 'left', textColorOverride, images, transforms, fotoRealTransform, isTextSelected, onSlotClick, onTextClick, onTextPointerDown }, ref) => {
        const light = isLight(bgColor);
        const textColor = textColorOverride || (light ? '#1a1a1a' : '#ffffff');
        const subColor = textColorOverride ? hexToRgba(textColorOverride, 0.72) : (light ? 'rgba(30,30,30,0.7)' : 'rgba(255,255,255,0.7)');
        const accentColor = light ? 'rgba(30,30,30,0.25)' : 'rgba(255,255,255,0.2)';
        const brandColor = light ? 'rgba(30,30,30,0.7)' : 'rgba(255,255,255,0.7)';

        const metaFontSize = fontSize * 0.5;    // 50% of quote size
        const brandFontSize = Math.max(11, fontSize * 0.38);

        const quoteText = `\u201c${data.text}\u201d`;   // " ... "

        // ── Overlay / decoration per template ──
        let overlay: React.ReactNode = null;
        let bgStyle: React.CSSProperties = { background: bgColor };

        if (template === 'minimalista') {
            bgStyle = {
                backgroundColor: bgColor,
                backgroundImage: DOT_TEXTURE,
            };
            overlay = (
                <>
                    {/* top accent bar */}
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: 4,
                        background: 'linear-gradient(90deg, rgba(196,120,82,0.95), rgba(196,120,82,0.35))'
                    }} />
                    {/* bottom accent bar */}
                    <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0, height: 4,
                        background: 'linear-gradient(90deg, rgba(196,120,82,0.95), rgba(196,120,82,0.35))'
                    }} />
                </>
            );
        } else if (template === 'papirus') {
            bgStyle = { background: bgColor };
            overlay = (
                <>
                    <div style={{ position: 'absolute', top: 30, left: 30, right: 30, height: 1, background: accentColor }} />
                    <div style={{ position: 'absolute', top: 36, left: 30, right: 30, height: 1, background: accentColor, opacity: 0.45 }} />
                </>
            );
        } else {
            // glass — use the user-selected bgColor as base
            bgStyle = { background: bgColor };
            overlay = (
                <>
                    {/* Blobs kept inside card bounds — no negative offsets that escape overflow:hidden */}
                    <div style={{
                        position: 'absolute', width: 420, height: 420, top: -60, left: -60, borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(124,58,237,0.55) 0%, transparent 70%)', filter: 'blur(55px)', pointerEvents: 'none'
                    }} />
                    <div style={{
                        position: 'absolute', width: 340, height: 340, bottom: -40, right: -40, borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(236,72,153,0.45) 0%, transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none'
                    }} />
                    <div style={{
                        position: 'absolute', width: 260, height: 260, top: '38%', left: '50%', borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(6,182,212,0.35) 0%, transparent 70%)', filter: 'blur(42px)', pointerEvents: 'none'
                    }} />
                </>
            );
        }

        const padding = 48;

        // ── Quote content ──
        const quoteContent = (
            <>
                <p style={{
                    fontFamily,
                    fontSize,
                    color: textColor,
                    fontWeight: isBold ? 900 : 700,
                    fontStyle: isItalic ? 'italic' : 'normal',
                    textDecoration: isUnderline ? 'underline' : 'none',
                    textAlign,
                    lineHeight: 1.65,
                    letterSpacing: '0.01em',
                    margin: 0,
                }}>
                    {quoteText}
                </p>

                {/* Book info — below quote, right-aligned, 50% size, 70% opacity */}
                <div style={{ marginTop: 28, textAlign: 'right' }}>
                    <p style={{ fontFamily, fontSize: metaFontSize, color: subColor, fontWeight: 700, margin: 0, lineHeight: 1.4 }}>
                        {data.bookTitle}
                    </p>
                    <p style={{ fontFamily, fontSize: metaFontSize, color: subColor, fontWeight: 400, margin: '4px 0 0', lineHeight: 1.4, fontStyle: 'italic' }}>
                        — {data.authorName}
                    </p>
                </div>
            </>
        );

        if (template.startsWith('foto-real')) {
            const bgUrl = FOTO_REAL_URLS[template] || FOTO_REAL_1_URL;
            const custom = fotoRealTransform || getDefaultFotoRealTransform(template);
            const textOpacity = custom.opacity ?? 0.85;
            const inkColor = textColorOverride ? hexToRgba(textColorOverride, textOpacity) : `rgba(35, 25, 18, ${textOpacity})`;
            // "Multiply" imita tinta impressa no papel, mas apagaria cores claras escolhidas pelo usuário.
            const inkBlend = textColorOverride ? 'normal' : 'multiply';
            const serifFont = fontFamily || "'Merriweather', 'Playfair Display', Georgia, serif";

            const topPos = typeof custom.top === 'number' ? `${custom.top}%` : custom.top;
            const leftPos = typeof custom.left === 'number' ? `${custom.left}%` : custom.left;
            const widthPos = typeof custom.width === 'number' ? `${custom.width}px` : custom.width;
            const rotateDeg = custom.rotation || 0;
            const fontScale = custom.fontScale || 1.0;
            const warpScale = custom.warpScale ?? 3.0;
            const effectiveAlign = custom.textAlign || (template === 'foto-real-2' ? 'center' : (textAlign || 'left'));

            return (
                <div ref={ref} style={{ width: CARD_W, height: CARD_H, position: 'relative', overflow: 'hidden', backgroundColor: '#e2d7c5' }}>
                    {/* Background Template Image */}
                    <img
                        src={bgUrl}
                        alt="Foto Real Background"
                        crossOrigin="anonymous"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 0
                        }}
                    />

                    {/* Bounded Text Container */}
                    <div 
                        onClick={onTextClick}
                        style={{
                            position: 'absolute',
                            top: topPos,
                            left: leftPos,
                            width: widthPos,
                            transform: `rotate(${rotateDeg}deg) skewX(${custom.skewX || 0}deg) skewY(${custom.skewY || 0}deg)`,
                            transformOrigin: 'top left',
                            zIndex: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: effectiveAlign === 'center' ? 'center' : (effectiveAlign === 'right' ? 'flex-end' : 'flex-start'),
                            overflow: 'visible',
                            boxSizing: 'border-box',
                            padding: '10px 14px',
                            cursor: 'pointer',
                            outline: isTextSelected ? '1.5px dashed rgba(234, 179, 8, 0.7)' : 'none',
                            borderRadius: '8px',
                            transition: 'outline 0.2s ease',
                            opacity: textOpacity,
                        }}
                    >
                        {/* Book Title as Header (Italic, Header style) */}
                        <h3 style={{
                            fontFamily: serifFont,
                            fontSize: Math.max(10, fontSize * 0.42 * fontScale),
                            fontStyle: 'italic',
                            fontWeight: 700,
                            color: inkColor,
                            mixBlendMode: inkBlend,
                            textAlign: effectiveAlign,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            margin: '0 0 10px 0',
                            lineHeight: 1.3,
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            overflow: 'visible'
                        }}>
                            <CurvedText text={data.bookTitle} curve={warpScale} highlight={isTextSelected} />
                        </h3>

                        {/* Main Quote Text */}
                        <p style={{
                            fontFamily: serifFont,
                            fontSize: fontSize * 0.7 * fontScale,
                            color: inkColor,
                            mixBlendMode: inkBlend,
                            fontWeight: isBold ? 900 : 600,
                            fontStyle: isItalic ? 'italic' : 'normal',
                            textDecoration: isUnderline ? 'underline' : 'none',
                            textAlign: effectiveAlign,
                            lineHeight: 1.45,
                            letterSpacing: '-0.01em',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            overflow: 'visible',
                            margin: 0,
                        }}>
                            <CurvedText text={quoteText} curve={warpScale} highlight={isTextSelected} />
                        </p>
                    </div>
                </div>
            );
        }

        if (template === 'collage-3') {
            return (
                <div ref={ref} style={{ width: CARD_W, height: CARD_H, position: 'relative', overflow: 'hidden', ...bgStyle, display: 'flex', flexDirection: 'column' }}>
                    <SlotWrapper onClick={() => onSlotClick?.(0)}>
                        {images?.[0] ? renderImage(images[0], transforms?.[0]) : <EmptySlot label="Top Photo" />}
                    </SlotWrapper>
                    <SlotWrapper onClick={() => onSlotClick?.(1)}>
                        {images?.[1] ? renderImage(images[1], transforms?.[1]) : <EmptySlot label="Mid Photo" />}
                    </SlotWrapper>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: padding }}>
                        {quoteContent}
                    </div>
                </div>
            );
        } else if (template === 'collage-h2') {
            return (
                <div ref={ref} style={{ width: CARD_W, height: CARD_H, position: 'relative', overflow: 'hidden', ...bgStyle, display: 'flex', flexDirection: 'column' }}>
                    <SlotWrapper onClick={() => onSlotClick?.(0)}>
                        {images?.[0] ? renderImage(images[0], transforms?.[0]) : <EmptySlot label="Photo" />}
                    </SlotWrapper>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: padding }}>
                        {quoteContent}
                    </div>
                </div>
            );
        } else if (template === 'collage-v2') {
            return (
                <div ref={ref} style={{ width: CARD_W, height: CARD_H, position: 'relative', overflow: 'hidden', ...bgStyle, display: 'flex', flexDirection: 'row' }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 24 }}>
                        {quoteContent}
                    </div>
                    <SlotWrapper onClick={() => onSlotClick?.(0)} style={{ borderLeft: '1px solid rgba(255,255,255,0.1)' }}>
                        {images?.[0] ? renderImage(images[0], transforms?.[0]) : <EmptySlot label="Photo" />}
                    </SlotWrapper>
                </div>
            );
        } else if (template === 'collage-4') {
            return (
                <div ref={ref} style={{ width: CARD_W, height: CARD_H, position: 'relative', overflow: 'hidden', ...bgStyle, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
                    <SlotWrapper onClick={() => onSlotClick?.(0)} style={{ borderRight: '1px solid rgba(255,255,255,0.1)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        {images?.[0] ? renderImage(images[0], transforms?.[0]) : <EmptySlot label="Photo 1" />}
                    </SlotWrapper>
                    <SlotWrapper onClick={() => onSlotClick?.(1)} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        {images?.[1] ? renderImage(images[1], transforms?.[1]) : <EmptySlot label="Photo 2" />}
                    </SlotWrapper>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 24, borderRight: '1px solid rgba(255,255,255,0.1)' }}>
                        {quoteContent}
                    </div>
                    <SlotWrapper onClick={() => onSlotClick?.(2)}>
                        {images?.[2] ? renderImage(images[2], transforms?.[2]) : <EmptySlot label="Photo 3" />}
                    </SlotWrapper>
                </div>
            );
        }

        return (
            <div ref={ref} style={{ width: CARD_W, height: CARD_H, position: 'relative', overflow: 'hidden', ...bgStyle }}>
                {/* BACKGROUND IMAGE SLOT (Slot 0 for non-collage templates) */}
                {!template.startsWith('collage') && (
                    <div
                        className="absolute inset-0 z-0 cursor-pointer overflow-hidden p-2"
                        onClick={() => onSlotClick?.(0)}
                    >
                        {images?.[0] && (
                            <div className="absolute inset-2 overflow-hidden rounded-[40px]">
                                {renderImage(images[0], transforms?.[0])}
                            </div>
                        )}
                    </div>
                )}

                {overlay}

                {template === 'glass' ? (
                    /* Glass: main content in frosted card */
                    <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', height: '100%', padding: `${padding}px` }}>
                        <div style={{
                            flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                            background: light ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.07)',
                            backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                            border: `1px solid ${light ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.12)'}`,
                            borderRadius: 32, padding: `40px 40px`,
                            boxShadow: '0 8px 48px rgba(0,0,0,0.3)',
                            marginBottom: 24,
                        }}>
                            {quoteContent}
                        </div>
                        {/* Watermark removido */}
                    </div>
                ) : (
                    /* Minimalista / Papirus */
                    <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', height: '100%', padding: `${padding + 10}px ${padding}px ${padding}px` }}>
                        {template === 'papirus' && (
                            <div style={{ textAlign: 'center', color: accentColor, fontSize: 22, letterSpacing: '0.4em', marginBottom: 36 }}>✦ ✦ ✦</div>
                        )}
                        {/* Centered quote */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            {quoteContent}
                        </div>
                        {/* Divider */}
                        {/* Watermark removido */}
                    </div>
                )}
            </div>
        );
    }
);

QuoteCard.displayName = 'QuoteCard';

// ─── Thumbnail ────────────────────────────────────────────────────────────────

const Thumbnail: React.FC<{
    template: Template; bgColor: string; selected: boolean; onClick: () => void; label: string;
}> = ({ template, bgColor, selected, onClick, label }) => {
    const light = isLight(bgColor);
    const tc = light ? '#1a1a1a' : '#fff';

    return (
        <button
            onClick={onClick}
            style={{
                width: 80, height: 142, position: 'relative', overflow: 'hidden', borderRadius: 16,
                border: selected ? '2px solid rgb(var(--ink))' : '2px solid transparent',
                backgroundColor: bgColor, cursor: 'pointer', outline: 'none',
                boxShadow: selected ? '0 0 0 3px rgb(var(--surface)), 0 10px 24px -10px rgba(0,0,0,0.5)' : '0 6px 18px -10px rgba(0,0,0,0.45)',
                transform: selected ? 'translateY(-2px)' : 'none',
                backgroundImage: template === 'minimalista' ? DOT_TEXTURE : undefined,
                transition: 'box-shadow 0.4s, border-color 0.4s, transform 0.4s cubic-bezier(0.22,1,0.36,1)',
                flexShrink: 0,
            }}
        >
            {template === 'glass' && (
                <>
                    <div style={{ position: 'absolute', width: 80, height: 80, top: -20, left: -15, borderRadius: '50%', background: 'radial-gradient(circle,rgba(124,58,237,0.5)0%,transparent 70%)', filter: 'blur(16px)' }} />
                    <div style={{ position: 'absolute', width: 60, height: 60, bottom: -10, right: -10, borderRadius: '50%', background: 'radial-gradient(circle,rgba(236,72,153,0.45)0%,transparent 70%)', filter: 'blur(14px)' }} />
                </>
            )}
            {template === 'minimalista' && (
                <>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, rgba(196,120,82,0.95), rgba(196,120,82,0.35))' }} />
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, rgba(196,120,82,0.95), rgba(196,120,82,0.35))' }} />
                </>
            )}
            {template === 'papirus' && (
                <>
                    <div style={{ position: 'absolute', top: 8, left: 6, right: 6, height: 1, background: `rgba(${light ? '0,0,0' : '255,255,255'},0.3)` }} />
                </>
            )}
            {template === 'collage-3' && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, borderBottom: '1px solid rgba(255,255,255,0.2)' }} />
                    <div style={{ flex: 1, borderBottom: '1px solid rgba(255,255,255,0.2)' }} />
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                        <div style={{ width: '60%', height: 2, background: tc, borderRadius: 2 }} />
                    </div>
                </div>
            )}
            {template === 'collage-h2' && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, borderBottom: '1px solid rgba(255,255,255,0.2)' }} />
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                        <div style={{ width: '60%', height: 2, background: tc, borderRadius: 2 }} />
                    </div>
                </div>
            )}
            {template === 'collage-v2' && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'row' }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                        <div style={{ width: '60%', height: 2, background: tc, borderRadius: 2 }} />
                    </div>
                    <div style={{ flex: 1, borderLeft: '1px solid rgba(255,255,255,0.2)' }} />
                </div>
            )}
            {template === 'collage-4' && (
                <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
                    <div style={{ borderRight: '1px solid rgba(255,255,255,0.2)', borderBottom: '1px solid rgba(255,255,255,0.2)' }} />
                    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }} />
                    <div style={{ borderRight: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                        <div style={{ width: '60%', height: 2, background: tc, borderRadius: 2 }} />
                    </div>
                    <div />
                </div>
            )}
            {template.startsWith('foto-real') && (
                <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                    <img 
                        src={FOTO_REAL_URLS[template] || FOTO_REAL_1_URL} 
                        alt="Foto Real" 
                        crossOrigin="anonymous"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                    <div style={{
                        position: 'absolute',
                        top: '28%',
                        left: '25%',
                        width: '50%',
                        height: '35%',
                        transform: 'rotate(-12deg)',
                        transformOrigin: 'top left',
                        display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center',
                        opacity: 0.92,
                        mixBlendMode: 'multiply'
                    }}>
                        {/* Mini title header */}
                        <div style={{ height: 2.5, background: '#1A1614', borderRadius: 1, width: '40%', marginBottom: 2 }} />
                        {/* Mini quote lines */}
                        <div style={{ height: 2, background: '#1A1614', borderRadius: 1, width: '90%' }} />
                        <div style={{ height: 2, background: '#1A1614', borderRadius: 1, width: '100%' }} />
                        <div style={{ height: 2, background: '#1A1614', borderRadius: 1, width: '75%' }} />
                    </div>
                </div>
            )}
            {/* mini quote lines (only on generic text templates) */}
            {!template.startsWith('collage') && !template.startsWith('foto-real') && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '8px 6px', gap: 3 }}>
                    {[1, 0.8, 0.6].map((o, i) => (
                        <div key={i} style={{ height: 2, borderRadius: 2, background: `rgba(${light ? '0,0,0' : '255,255,255'},${o * 0.4})`, width: `${100 - i * 15}%` }} />
                    ))}
                </div>
            )}
            {selected && (
                <div style={{ position: 'absolute', top: 6, right: 6, width: 16, height: 16, borderRadius: '50%', background: 'rgb(var(--ink))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Check size={10} color="rgb(var(--surface))" strokeWidth={2.5} />
                </div>
            )}
            <div style={{ position: 'absolute', bottom: 5, left: 0, right: 0, textAlign: 'center', fontSize: 7, fontWeight: 700, color: tc, opacity: 0.6, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {label}
            </div>
        </button>
    );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

const QuoteCardModal: React.FC<Props> = ({ data, onClose }) => {
    const { t, i18n } = useTranslation();
    const L = getQuoteCardText(i18n.language);
    const { isDarkMode } = useTheme();
    const { accentColor } = useTheme();
    const [template, setTemplate] = useState<Template>('minimalista');
    const [bgColorId, setBgColorId] = useState('bk');   // preset id, or 'custom'
    const [customBgColor, setCustomBgColor] = useState('#161615');
    const [fontId, setFontId] = useState('playfair');
    const [fontSizeId, setFontSizeId] = useState('md');
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [textAlign, setTextAlign] = useState<'left' | 'center' | 'right'>('left');
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);
    const [canShare, setCanShare] = useState(false);
    const [activeEditModal, setActiveEditModal] = useState<'template' | 'color' | 'text' | 'textColor' | 'font' | 'size' | null>(null);
    const [textColorOverride, setTextColorOverride] = useState<string | null>(null);
    const [isTextColorWheelOpen, setIsTextColorWheelOpen] = useState(false);
    const [isCustomColorPickerOpen, setIsCustomColorPickerOpen] = useState(false);
    const [isSocialShareOpen, setIsSocialShareOpen] = useState(false);
    const [generatedBlob, setGeneratedBlob] = useState<Blob | null>(null);
    const [showBranding, setShowBranding] = useState(true);
    const [scale, setScale] = useState(0.42);
    // Confirmação antes de compartilhar/baixar e marca d'água "libritui." opcional
    const [pendingExport, setPendingExport] = useState<'share' | 'download' | null>(null);
    const [useWatermark, setUseWatermark] = useState(true);
    const [stampWatermark, setStampWatermark] = useState(false);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const captureRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Image Upload State 
    const [images, setImages] = useState<Record<number, string>>({});
    const [transforms, setTransforms] = useState<Record<number, ImageTransform>>({});
    const [activeImageSlot, setActiveImageSlot] = useState<number | null>(null);
    const [isCreativeMode, setIsCreativeMode] = useState(false);
    const [drawingPaths, setDrawingPaths] = useState<any[]>([]);
    const [currentPath, setCurrentPath] = useState<any>(null);
    const [brushColor, setBrushColor] = useState('#ffffff');
    const [brushSize, setBrushSize] = useState(4);
    const [brushOpacity, setBrushOpacity] = useState(1);
    const [activeCreativeTool, setActiveCreativeTool] = useState<'color' | 'size' | 'opacity' | null>(null);

    useEffect(() => {
        if (!isCreativeMode) setActiveCreativeTool(null);
    }, [isCreativeMode]);

    // Foto Real Interactive Text State
    const [fotoRealTransforms, setFotoRealTransforms] = useState<Record<string, FotoRealTransform>>(DEFAULT_FOTO_REAL_TRANSFORMS);
    const [isFotoRealTextSelected, setIsFotoRealTextSelected] = useState<boolean>(true);
    const [fotoRealSnapshot, setFotoRealSnapshot] = useState<FotoRealTransform | null>(null);
    const [activeSliderControl, setActiveSliderControl] = useState<'rotation' | 'width' | 'warpScale' | 'opacity' | null>(null);
    const [activeFotoRealDrag, setActiveFotoRealDrag] = useState<{ handle: 'move' | 'rotate' | 'resize' | 'p1' | 'p2' | 'p3' | 'p4'; startX: number; startY: number; initial: FotoRealTransform } | null>(null);

    // Auto-select text & take snapshot when selecting a foto-real template
    useEffect(() => {
        if (template.startsWith('foto-real')) {
            setIsFotoRealTextSelected(true);
            setActiveSliderControl(null);
            const currentTf = fotoRealTransforms[template] || getDefaultFotoRealTransform(template);
            setFotoRealSnapshot({ ...currentTf });
        } else {
            setIsFotoRealTextSelected(false);
            setActiveSliderControl(null);
        }
    }, [template]);

    // Preload ALL 7 templates
    useEffect(() => {
        Object.values(FOTO_REAL_URLS).forEach(url => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = url;
        });
    }, []);

    // Pointer drag handlers for Foto Real interactive controls
    const handleFotoRealPointerDown = (handle: 'move' | 'rotate' | 'resize' | 'p1' | 'p2' | 'p3' | 'p4', e: React.PointerEvent) => {
        e.stopPropagation();
        const currentTransform = fotoRealTransforms[template] || getDefaultFotoRealTransform(template);
        setActiveFotoRealDrag({
            handle,
            startX: e.clientX,
            startY: e.clientY,
            initial: { ...currentTransform }
        });
        try {
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
        } catch (_) {}
    };

    useEffect(() => {
        if (!activeFotoRealDrag) return;

        const handleGlobalPointerMove = (e: PointerEvent) => {
            const { handle, startX, startY, initial } = activeFotoRealDrag;
            const dx = (e.clientX - startX) / (scale || 1);
            const dy = (e.clientY - startY) / (scale || 1);

            setFotoRealTransforms(prev => {
                const current = prev[template] || getDefaultFotoRealTransform(template);
                const updated = { ...current };

                if (handle === 'move') {
                    updated.left = Math.min(85, Math.max(2, initial.left + (dx / CARD_W) * 100));
                    updated.top = Math.min(85, Math.max(2, initial.top + (dy / CARD_H) * 100));
                } else if (handle === 'rotate') {
                    updated.rotation = Math.round((initial.rotation + dx * 0.5) % 360);
                } else if (handle === 'resize') {
                    updated.width = Math.round(Math.min(490, Math.max(120, initial.width + dx)));
                    updated.fontScale = Math.min(2.8, Math.max(0.3, initial.fontScale * (updated.width / (initial.width || 340)) + dy * 0.005));
                } else if (handle === 'p1' || handle === 'p2' || handle === 'p3' || handle === 'p4') {
                    updated.warpScale = Math.min(15, Math.max(-15, initial.warpScale + dy * 0.2));
                    updated.skewX = Math.min(50, Math.max(-50, initial.skewX + dx * 0.3));
                    updated.skewY = Math.min(50, Math.max(-50, initial.skewY + dy * 0.3));
                }

                return { ...prev, [template]: updated };
            });
        };

        const handleGlobalPointerUp = () => {
            setActiveFotoRealDrag(null);
        };

        window.addEventListener('pointermove', handleGlobalPointerMove);
        window.addEventListener('pointerup', handleGlobalPointerUp);
        return () => {
            window.removeEventListener('pointermove', handleGlobalPointerMove);
            window.removeEventListener('pointerup', handleGlobalPointerUp);
        };
    }, [activeFotoRealDrag, scale, template]);

    useEffect(() => {
        if (template.startsWith('foto-real')) {
            const currentF = FONTS.find(f => f.id === fontId);
            if (!currentF || currentF.family.includes('sans-serif') || currentF.family.includes('cursive')) {
                setFontId('merriweather');
            }
        }
    }, [template]);

    // The actual hex used for the card — preset OR custom picker
    const bgColor = bgColorId === 'custom'
        ? customBgColor
        : (BG_COLORS.find(c => c.id === bgColorId)?.value ?? '#161615');
    const fontFamily = FONTS.find(f => f.id === fontId)?.family ?? 'Inter, sans-serif';
    const fontSize = FONT_SIZES.find(f => f.id === fontSizeId)?.px ?? 28;
    const currentFont = FONTS.find(f => f.id === fontId)!;

    useEffect(() => {
        const testFile = new File([''], 'test.jpg', { type: 'image/jpeg' });
        setCanShare(!!(navigator.canShare && navigator.canShare({ files: [testFile] })));
    }, []);

    // Preload Google Font for current selection in the browser
    useEffect(() => {
        if (!currentFont?.gQuery) return;
        const id = `gf-${fontId}`;
        if (document.getElementById(id)) return;
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${currentFont.gQuery}&display=swap`;
        document.head.appendChild(link);
    }, [fontId, currentFont]);

    // Scale preview to fit viewport dynamically (90% rule)
    useEffect(() => {
        const update = () => {
            if (!containerRef.current) return;

            // Get available space in the central container
            const { clientWidth: availableW, clientHeight: availableH } = containerRef.current;

            // Apply safety limits (90vw / 70vh) to the available area itself if needed, 
            // but the request is specifically for the element relative to the space.
            // We'll target 90% of the available box.
            const targetW = availableW * 0.9;
            const targetH = availableH * 0.9;

            // Calculate scale to fit while maintaining aspect ratio (540x960)
            const scaleW = targetW / CARD_W;
            const scaleH = targetH / CARD_H;

            // Use the smaller scale to ensure it fits both ways
            const finalScale = Math.min(scaleW, scaleH);

            // Maximum scale cap (e.g., don't go too crazy on massive screens)
            setScale(Math.min(finalScale, 0.8)); // 0.8 is quite large for desktop
        };

        const resizeObserver = new ResizeObserver(update);
        if (containerRef.current) resizeObserver.observe(containerRef.current);

        window.addEventListener('resize', update);
        update(); // Initial calculation

        return () => {
            window.removeEventListener('resize', update);
            resizeObserver.disconnect();
        };
    }, []);

    const handleSlotClick = useCallback((index: number) => {
        setActiveImageSlot(index);
        // Only trigger file upload if slot is empty
        if (!images[index]) {
            fileInputRef.current?.click();
        }
    }, [images]);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && activeImageSlot !== null) {
            try {
                // 1. Rate Limit
                importRateLimiter.checkLimit();

                // 2. Format Validation
                const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
                if (!allowed.includes(file.type)) {
                    throw new Error("Formato inválido. Use PNG, JPG ou JPEG.");
                }

                // 3. Size Validation (15MB)
                if (file.size > 15 * 1024 * 1024) {
                    throw new Error("Imagem muito grande. O limite é de 15MB.");
                }

                const reader = new FileReader();
                reader.onload = (ev) => {
                    if (ev.target?.result && typeof ev.target.result === 'string') {
                        setImages(prev => ({ ...prev, [activeImageSlot]: ev.target.result as string }));
                    }
                };
                reader.readAsDataURL(file);
            } catch (err: any) {
                alert(err.message);
            }
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [activeImageSlot]);

    // Drawing Logic
    const startDrawing = (e: React.PointerEvent) => {
        if (!isCreativeMode) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;
        setCurrentPath({
            points: [{ x, y }],
            color: brushColor,
            size: brushSize,
            opacity: brushOpacity
        });
    };

    const draw = (e: React.PointerEvent) => {
        if (!isCreativeMode || !currentPath) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;
        setCurrentPath(prev => ({ ...prev, points: [...prev.points, { x, y }] }));
    };

    const endDrawing = () => {
        if (currentPath) {
            setDrawingPaths(prev => [...prev, currentPath]);
            setCurrentPath(null);
        }
    };

    const undoLastDraw = () => setDrawingPaths(prev => prev.slice(0, -1));

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const renderPath = (path: any) => {
            if (path.points.length < 2) return;
            ctx.beginPath();
            ctx.globalAlpha = path.opacity ?? 1;
            ctx.strokeStyle = path.color;
            ctx.lineWidth = path.size;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.moveTo(path.points[0].x, path.points[0].y);
            for (let i = 1; i < path.points.length; i++) {
                ctx.lineTo(path.points[i].x, path.points[i].y);
            }
            ctx.stroke();
        };

        drawingPaths.forEach(renderPath);
        if (currentPath) renderPath(currentPath);
    }, [drawingPaths, currentPath, isCreativeMode]);

    /** Build JPEG blob from card element */
    const getBlob = useCallback(async (): Promise<Blob | null> => {
        if (!captureRef.current) return null;

        // Embed fonts as base64 to avoid CORS cssRules SecurityError
        let fontEmbedCSS = '';
        if (currentFont.gQuery) {
            fontEmbedCSS = await fetchEmbeddedFontCSS(currentFont.gQuery);
        }
        if (stampWatermark) {
            fontEmbedCSS += await fetchEmbeddedFontCSS('Geist:wght@500');
        }

        try {
            const dataUrl = await toJpeg(captureRef.current, {
                quality: 0.97,
                width: CARD_W,
                height: CARD_H,
                pixelRatio: 2,            // → 1080×1920 output
                cacheBust: true,
                useCORS: true,            // Requisitado no protocolo
                allowTaint: true,         // Requisitado no protocolo
                fontEmbedCSS: fontEmbedCSS || undefined,
                // Skip cross-origin stylesheets to prevent SecurityError
                filter: (node: Element) => {
                    if (node.tagName === 'LINK') {
                        const href = (node as HTMLLinkElement).href || '';
                        if (href.includes('fonts.googleapis.com')) return false;
                    }
                    if (node.classList?.contains('no-export')) return false;
                    return true;
                },
            });
            const res = await fetch(dataUrl);
            return await res.blob();
        } catch (err) {
            console.error('Export error:', err);
            // Fallback: try without font embed (accepts loss of custom font)
            try {
                const dataUrl = await toJpeg(captureRef.current, {
                    quality: 0.97, width: CARD_W, height: CARD_H, pixelRatio: 2, cacheBust: true,
                    skipFonts: true,
                });
                const res = await fetch(dataUrl);
                return await res.blob();
            } catch {
                return null;
            }
        }
    }, [currentFont, stampWatermark]);
    const getBlobRef = useRef(getBlob);
    getBlobRef.current = getBlob;

    // Liga a marca d'água, espera o React desenhar e só então captura
    const captureWith = useCallback(async (withMark: boolean) => {
        setStampWatermark(withMark);
        await new Promise(r => setTimeout(r, 120));
        return null;
    }, []);

    const handleSave = useCallback(async (withMark: boolean = true) => {
        setIsExporting(true);
        setExportError(null);
        try {
            await captureWith(withMark);
            const blob = await getBlobRef.current();
            if (!blob) throw new Error(L.errorGenerate);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `libritui-${Date.now()}.jpg`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e: any) {
            setExportError(e.message || L.errorGenerate);
        } finally {
            setStampWatermark(false);
            setIsExporting(false);
        }
    }, [captureWith, L]);

    const handleShare = useCallback(async (withMark: boolean = true) => {
        setIsExporting(true);
        setExportError(null);
        try {
            await captureWith(withMark);
            const blob = await getBlobRef.current();
            if (!blob) throw new Error(L.errorGenerate);
            const file = new File([blob], 'libritui-citacao.jpg', { type: 'image/jpeg' });
            await navigator.share({
                files: [file],
                title: `"${data.text.substring(0, 60)}"`,
                text: `${data.bookTitle} – ${data.authorName}`,
            });
        } catch (e: any) {
            if (e?.name !== 'AbortError') setExportError(e.message || L.errorGenerate);
        } finally {
            setStampWatermark(false);
            setIsExporting(false);
        }
    }, [data, captureWith, L]);

    const handleOpenSocialShare = useCallback(async () => {
        setIsExporting(true);
        setExportError(null);
        
        // Disable branding for community feed shares
        setShowBranding(false);

        try {
            // Small delay to ensure React renders the change
            await new Promise(resolve => setTimeout(resolve, 100));

            const blob = await getBlob();
            if (!blob) throw new Error('Falha ao gerar preview da postagem');

            // Re-enable branding for other actions
            setShowBranding(true);

            setGeneratedBlob(blob);
            setIsSocialShareOpen(true);
        } catch (e: any) {
            console.error('Social share capture error:', e);
            setExportError(e.message || 'Erro ao preparar imagem para a rede');
            setShowBranding(true);
        } finally {
            setIsExporting(false);
        }
    }, [getBlob]);

    const Spinner = () => (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin opacity-70" />
    );

    const setFotoRealWidth = (w: number) => setFotoRealTransforms(prev => {
        const cur = prev[template] || getDefaultFotoRealTransform(template);
        const width = Math.round(Math.min(520, Math.max(160, w)));
        const ratio = width / (cur.width || 340);
        return { ...prev, [template]: { ...cur, width, fontScale: Math.min(2.8, Math.max(0.3, (cur.fontScale || 1) * ratio)) } };
    });

    const currentTransform = activeImageSlot !== null ? (transforms[activeImageSlot] || { scale: 1, x: 0, y: 0, rotate: 0, flipX: false }) : null;

    const updateTransform = (slot: number, newTransform: Partial<ImageTransform>) => {
        setTransforms(prev => ({
            ...prev,
            [slot]: { ...(prev[slot] || { scale: 1, x: 0, y: 0, rotate: 0, flipX: false }), ...newTransform }
        }));
    };

    const clearImageSlot = (slot: number) => {
        const newImages = { ...images };
        delete newImages[slot];
        setImages(newImages);
        const newTransforms = { ...transforms };
        delete newTransforms[slot];
        setTransforms(newTransforms);
        setActiveImageSlot(null);
    };

    const rotateImage = (slot: number) => {
        const current = transforms[slot] || { scale: 1, x: 0, y: 0, rotate: 0, flipX: false };
        updateTransform(slot, { rotate: current.rotate + 90 });
    };

    const moveImage = (slot: number, dx: number, dy: number) => {
        const trans = transforms[slot] || { scale: 1, x: 0, y: 0, rotate: 0, flipX: false };
        const rad = (trans.rotate * Math.PI) / 180;

        // Map screen-space movement (dx, dy) to internal image-space coordinates (x, y) 
        // considering the current rotation.
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);

        const newX = trans.x + (dx * cos + dy * sin);
        const newY = trans.y + (-dx * sin + dy * cos);

        updateTransform(slot, { x: newX, y: newY });
    };

    return ReactDOM.createPortal((
        <div
            className="fixed inset-0 z-[500] flex flex-row bg-surface text-ink overflow-hidden animate-in fade-in duration-200 select-none"
            onClick={e => e.target === e.currentTarget && onClose()}
        >
            {/* ── SIDEBAR SECTIONS (Left) ── */}
            <div className="relative w-[72px] md:w-24 bg-surface border-r border-black/[0.06] dark:border-white/[0.06] flex flex-col items-center z-[60] shrink-0 transition-all duration-300">
                {/* ── MAIN SIDEBAR (Estilo, Cor, etc) ── */}
                <div className={`absolute inset-0 flex flex-col items-center py-4 md:py-8 transition-all duration-300 ${((activeImageSlot !== null && images[activeImageSlot!]) || isCreativeMode) ? '-translate-x-[120%] opacity-0 pointer-events-none invisible' : 'translate-x-0 opacity-100 pointer-events-auto visible'}`}>
                    <button
                        onClick={onClose}
                        aria-label={L.back}
                        className="group w-10 h-10 mb-5 inline-flex items-center justify-center rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone transition-colors duration-300 shrink-0"
                    >
                        <ArrowLeft size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
                    </button>

                    <div className="flex-1 w-full flex flex-col items-center gap-4 overflow-y-auto no-scrollbar pb-4 shrink-0">
                        {[
                            { id: 'template' as const, icon: Layout, label: L.style, anim: 'group-hover:scale-110' },
                            { id: 'color' as const, icon: Palette, label: L.color, anim: 'group-hover:rotate-[20deg]' },
                            { id: 'text' as const, icon: Type, label: L.text, anim: 'group-hover:-translate-y-0.5' },
                            { id: 'textColor' as const, icon: Baseline, label: L.textColor, anim: 'group-hover:scale-110' },
                            { id: 'font' as const, icon: CaseSensitive, label: L.font, anim: 'group-hover:scale-110' },
                            { id: 'size' as const, icon: Maximize2, label: L.size, anim: 'group-hover:scale-125' },
                            { id: 'creative' as const, icon: Pencil, label: L.creative, anim: 'group-hover:-rotate-12' },
                        ].map(item => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    if (item.id === 'creative') {
                                        setIsCreativeMode(!isCreativeMode);
                                        setActiveEditModal(null);
                                    } else {
                                        setActiveEditModal(item.id);
                                        setIsCreativeMode(false);
                                    }
                                }}
                                className="group flex flex-col items-center gap-1.5 shrink-0"
                            >
                                <span className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${(activeEditModal === item.id || (item.id === 'creative' && isCreativeMode)) ? 'bg-ink text-surface' : 'text-muted group-hover:text-ink group-hover:bg-tint-stone'}`}>
                                    <item.icon size={18} strokeWidth={1.75} className={`transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${item.anim}`} />
                                </span>
                                <span className={`text-[10px] font-medium transition-colors duration-300 ${(activeEditModal === item.id || (item.id === 'creative' && isCreativeMode)) ? 'text-ink' : 'text-muted group-hover:text-ink'}`}>{item.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── IMAGE CONTROLS SIDEBAR ── */}
                <div className={`absolute inset-0 flex flex-col items-center p-2 py-4 md:py-8 no-scrollbar transition-all duration-300 ${activeImageSlot !== null && images[activeImageSlot] && !isCreativeMode ? 'translate-x-0 opacity-100 pointer-events-auto visible' : 'translate-x-[120%] opacity-0 pointer-events-none invisible'}`}>
                    {activeImageSlot !== null && currentTransform && (
                        <div className="flex-1 w-full flex flex-col items-center gap-4">
                            <span className="text-[8px] font-normal uppercase font-mono tracking-[0.14em] text-muted mb-2 shrink-0 text-center">
                                {L.editPhoto}
                            </span>

                            {/* D-Pad Controls */}
                            <div className="flex flex-col gap-1 p-1 bg-tint-stone/40 rounded-[22px] shrink-0">
                                <button onClick={() => moveImage(activeImageSlot!, 0, -16)} className="p-1.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all flex items-center justify-center shrink-0">
                                    <ArrowUp size={14} className="text-ink" />
                                </button>
                                <button onClick={() => moveImage(activeImageSlot!, 0, 16)} className="p-1.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all flex items-center justify-center shrink-0">
                                    <ArrowDown size={14} className="text-ink" />
                                </button>
                                <button onClick={() => moveImage(activeImageSlot!, -16, 0)} className="p-1.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all flex items-center justify-center shrink-0">
                                    <ArrowLeft size={14} className="text-ink" />
                                </button>
                                <button onClick={() => moveImage(activeImageSlot!, 16, 0)} className="p-1.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all flex items-center justify-center shrink-0">
                                    <ArrowRight size={14} className="text-ink" />
                                </button>
                            </div>

                            <div className="w-8 h-px bg-black/[0.06] dark:bg-white/[0.08] shrink-0" />

                            <span className="text-[8px] font-normal uppercase font-mono tracking-[0.14em] text-muted mb-1 shrink-0 text-center">
                                {L.zoom}
                            </span>

                            {/* Zoom +/- */}
                            <div className="flex flex-col gap-2 shrink-0">
                                <button onClick={() => updateTransform(activeImageSlot!, { scale: Math.min(3, currentTransform.scale + 0.1) })} className="p-2.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all shrink-0">
                                    <Plus size={16} className="text-ink" />
                                </button>
                                <button onClick={() => updateTransform(activeImageSlot!, { scale: Math.max(0.1, currentTransform.scale - 0.1) })} className="p-2.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all shrink-0">
                                    <Minus size={16} className="text-ink" />
                                </button>
                            </div>

                            <div className="w-8 h-px bg-black/[0.06] dark:bg-white/[0.08] shrink-0" />

                            {/* Rotate & Flip */}
                            <div className="flex flex-col gap-2 shrink-0">
                                <button onClick={() => rotateImage(activeImageSlot!)} className="p-2.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all shrink-0">
                                    <RotateCw size={16} className="text-ink" />
                                </button>
                                <button onClick={() => updateTransform(activeImageSlot!, { flipX: !currentTransform.flipX })} className="p-2.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all shrink-0">
                                    <FlipHorizontal size={16} className="text-ink" />
                                </button>
                            </div>

                            <div className="flex-1 min-h-[10px] shrink-0" />

                            {/* ACTIONS (Delete / Confirm) */}
                            <div className="flex flex-col gap-2 mt-auto shrink-0 pt-2 pb-4">
                                <button onClick={() => clearImageSlot(activeImageSlot!)} className="p-2.5 bg-tint-rose/60 hover:bg-tint-rose text-bad rounded-full transition-all shrink-0">
                                    <Trash2 size={16} />
                                </button>
                                <button onClick={() => setActiveImageSlot(null)} className="p-2.5 bg-primary text-slate-900 hover:opacity-90 rounded-full transition-all shrink-0">
                                    <Check size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── CREATIVE CONTROLS SIDEBAR ── */}
                <div className={`absolute inset-0 flex flex-col items-center p-2 py-4 md:py-8 no-scrollbar transition-all duration-300 ${isCreativeMode ? 'translate-x-0 opacity-100 pointer-events-auto visible' : 'translate-x-[120%] opacity-0 pointer-events-none invisible'}`}>
                    <div className="flex-1 w-full flex flex-col items-center gap-4">
                        <span className="text-[8px] font-normal uppercase font-mono tracking-[0.14em] text-muted mb-2 shrink-0 text-center">
                            {L.creativeMode}
                        </span>

                        {/* Creative Tools */}
                        <div className="flex flex-col gap-4">
                            {/* Color Picker Button */}
                            <div className="relative group">
                                <button
                                    onClick={() => setActiveCreativeTool(activeCreativeTool === 'color' ? null : 'color')}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${activeCreativeTool === 'color' ? 'bg-ink text-surface' : 'bg-tint-stone/60 text-muted hover:text-ink hover:bg-tint-stone'}`}
                                    title={L.colorTool}
                                >
                                    <Palette size={20} />
                                </button>
                                {activeCreativeTool === 'color' && (
                                    <div className="absolute left-[110%] top-0 p-3 bg-surface text-ink border border-black/[0.06] dark:border-white/[0.06] rounded-[20px] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] z-[70] flex flex-col gap-2 min-w-[140px] animate-in slide-in-from-left-2 duration-200 backdrop-blur-xl">
                                        <div className="grid grid-cols-4 gap-2">
                                            {['#ffffff', '#000000', '#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#ec4899', '#8b5cf6'].map(c => (
                                                <button
                                                    key={c}
                                                    onClick={() => setBrushColor(c)}
                                                    className={`w-6 h-6 rounded-full transition-all ${brushColor === c ? 'scale-125 ring-2 ring-ink ring-offset-2 ring-offset-surface' : 'hover:scale-110'}`}
                                                    style={{ backgroundColor: c }}
                                                />
                                            ))}
                                        </div>
                                        <div className="h-px bg-black/[0.06] dark:bg-white/[0.08] my-1" />
                                        <label className="flex flex-col gap-1 cursor-pointer">
                                            <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em] text-muted">{L.custom}</span>
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full border border-black/10 dark:border-white/20" style={{ backgroundColor: brushColor }} />
                                                <input
                                                    type="color"
                                                    value={brushColor}
                                                    onChange={(e) => setBrushColor(e.target.value)}
                                                    className="w-full h-6 bg-transparent border-none cursor-pointer p-0"
                                                />
                                            </div>
                                        </label>
                                    </div>
                                )}
                            </div>

                            {/* Thickness Button */}
                            <div className="relative group">
                                <button
                                    onClick={() => setActiveCreativeTool(activeCreativeTool === 'size' ? null : 'size')}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${activeCreativeTool === 'size' ? 'bg-ink text-surface' : 'bg-tint-stone/60 text-muted hover:text-ink hover:bg-tint-stone'}`}
                                    title={L.thickness}
                                >
                                    <Circle size={Math.min(24, brushSize + 10)} strokeWidth={3} />
                                </button>
                                {activeCreativeTool === 'size' && (
                                    <div className="absolute left-[110%] top-0 p-4 bg-surface text-ink border border-black/[0.06] dark:border-white/[0.06] rounded-[20px] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] z-[70] flex flex-col gap-3 min-w-[120px] animate-in slide-in-from-left-2 duration-200 backdrop-blur-xl">
                                        <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em] text-muted">{L.thickness}</span>
                                        <input
                                            type="range" min="1" max="50" step="1"
                                            value={brushSize}
                                            onChange={(e) => setBrushSize(Number(e.target.value))}
                                            className="w-24 h-1.5 bg-ink/10 rounded-full appearance-none cursor-pointer accent-primary"
                                        />
                                        <div className="flex justify-between text-[9px] font-mono tabular-nums text-muted">
                                            <span>1px</span>
                                            <span>{brushSize}px</span>
                                            <span>50px</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Opacity Button */}
                            <div className="relative group">
                                <button
                                    onClick={() => setActiveCreativeTool(activeCreativeTool === 'opacity' ? null : 'opacity')}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${activeCreativeTool === 'opacity' ? 'bg-ink text-surface' : 'bg-tint-stone/60 text-muted hover:text-ink hover:bg-tint-stone'}`}
                                    title={L.opacity}
                                >
                                    <Layers size={20} style={{ opacity: brushOpacity }} />
                                </button>
                                {activeCreativeTool === 'opacity' && (
                                    <div className="absolute left-[110%] top-0 p-4 bg-surface text-ink border border-black/[0.06] dark:border-white/[0.06] rounded-[20px] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] z-[70] flex flex-col gap-3 min-w-[120px] animate-in slide-in-from-left-2 duration-200 backdrop-blur-xl">
                                        <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em] text-muted">{L.opacity}</span>
                                        <input
                                            type="range" min="0.1" max="1" step="0.05"
                                            value={brushOpacity}
                                            onChange={(e) => setBrushOpacity(Number(e.target.value))}
                                            className="w-24 h-1.5 bg-ink/10 rounded-full appearance-none cursor-pointer accent-primary"
                                        />
                                        <div className="flex justify-between text-[9px] font-mono tabular-nums text-muted">
                                            <span>10%</span>
                                            <span>{Math.round(brushOpacity * 100)}%</span>
                                            <span>100%</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="w-8 h-px bg-black/[0.06] dark:bg-white/[0.08] my-2 shrink-0" />

                        <button onClick={undoLastDraw} className="p-2.5 bg-tint-rose/60 hover:bg-tint-rose text-bad rounded-full transition-all shrink-0" title={L.undo}>
                            <RotateCcw size={18} />
                        </button>

                        <div className="flex-1 min-h-[10px] shrink-0" />

                        {/* ACTIONS (Exit / Save) */}
                        <div className="flex flex-col gap-2 mt-auto shrink-0 pt-2 pb-4">
                            <button onClick={() => { setIsCreativeMode(false); setActiveCreativeTool(null); }} className="p-2.5 bg-tint-rose/60 hover:bg-tint-rose text-bad rounded-full transition-all shrink-0">
                                <X size={16} />
                            </button>
                            <button onClick={() => { setIsCreativeMode(false); setActiveCreativeTool(null); }} className="p-2.5 bg-primary text-slate-900 hover:opacity-90 rounded-full transition-all shrink-0">
                                <Check size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── MAIN CONTENT (Preview Area) ── */}
            <div className="flex-1 relative flex flex-col overflow-hidden bg-slate-100 dark:bg-slate-950">
                {/* Floating Re-Open Resize Button (Upper Right) */}
                {template.startsWith('foto-real') && !isFotoRealTextSelected && (
                    <button
                        onClick={() => {
                            setIsFotoRealTextSelected(true);
                            const currentTf = fotoRealTransforms[template] || getDefaultFotoRealTransform(template);
                            setFotoRealSnapshot({ ...currentTf });
                        }}
                        className="absolute top-4 right-4 z-50 p-3 bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink rounded-full shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] backdrop-blur-md hover:scale-105 hover:bg-tint-stone active:scale-95 transition-all duration-300 flex items-center justify-center animate-in fade-in zoom-in-95 no-export"
                        title={L.editText}
                    >
                        <Maximize2 size={18} strokeWidth={1.75} className="text-ink" />
                    </button>
                )}

                {/* Floating Top Slider Bar for Rotação / Tamanho / Curva / Opacidade */}
                {template.startsWith('foto-real') && isFotoRealTextSelected && activeSliderControl !== null && (
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-surface/95 text-ink border border-black/[0.06] dark:border-white/[0.08] backdrop-blur-xl px-4 py-2 rounded-full flex items-center gap-2.5 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] animate-in slide-in-from-top-4 fade-in duration-300 no-export select-none">
                        {activeSliderControl === 'rotation' && (
                            <>
                                <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-muted whitespace-nowrap min-w-[85px]">
                                    {L.rotation}: {fotoRealTransforms[template]?.rotation || 0}°
                                </span>
                                <div className="flex items-center gap-1 border-r border-black/[0.06] dark:border-white/[0.08] pr-2">
                                    <button
                                        onClick={() => setFotoRealTransforms(prev => ({
                                            ...prev,
                                            [template]: { ...prev[template], rotation: Math.max(-60, (prev[template]?.rotation || 0) - 1) }
                                        }))}
                                        className="w-7 h-7 flex items-center justify-center bg-tint-stone/60 hover:bg-tint-stone active:scale-90 text-ink rounded-full transition-all"
                                        title="Diminuir 1°"
                                    >
                                        <Minus size={12} />
                                    </button>
                                    <button
                                        onClick={() => setFotoRealTransforms(prev => ({
                                            ...prev,
                                            [template]: { ...prev[template], rotation: Math.min(60, (prev[template]?.rotation || 0) + 1) }
                                        }))}
                                        className="w-7 h-7 flex items-center justify-center bg-tint-stone/60 hover:bg-tint-stone active:scale-90 text-ink rounded-full transition-all"
                                        title="Aumentar 1°"
                                    >
                                        <Plus size={12} />
                                    </button>
                                </div>
                                <input
                                    type="range"
                                    min={-60}
                                    max={60}
                                    step={1}
                                    value={fotoRealTransforms[template]?.rotation || 0}
                                    onChange={(e) => setFotoRealTransforms(prev => ({
                                        ...prev,
                                        [template]: { ...prev[template], rotation: parseInt(e.target.value) }
                                    }))}
                                    className="w-28 md:w-44 accent-primary cursor-pointer"
                                />
                            </>
                        )}
                        {activeSliderControl === 'width' && (
                            <>
                                <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-muted whitespace-nowrap min-w-[85px]">
                                    {L.boxSize}: {fotoRealTransforms[template]?.width || 340}px
                                </span>
                                <div className="flex items-center gap-1 border-r border-black/[0.06] dark:border-white/[0.08] pr-2">
                                    <button
                                        onClick={() => setFotoRealWidth((fotoRealTransforms[template]?.width || 340) - 5)}
                                        className="w-7 h-7 flex items-center justify-center bg-tint-stone/60 hover:bg-tint-stone active:scale-90 text-ink rounded-full transition-all"
                                        title="Diminuir 5px"
                                    >
                                        <Minus size={12} />
                                    </button>
                                    <button
                                        onClick={() => setFotoRealWidth((fotoRealTransforms[template]?.width || 340) + 5)}
                                        className="w-7 h-7 flex items-center justify-center bg-tint-stone/60 hover:bg-tint-stone active:scale-90 text-ink rounded-full transition-all"
                                        title="Aumentar 5px"
                                    >
                                        <Plus size={12} />
                                    </button>
                                </div>
                                <input
                                    type="range"
                                    min={160}
                                    max={520}
                                    step={5}
                                    value={fotoRealTransforms[template]?.width || 340}
                                    onChange={(e) => setFotoRealWidth(parseInt(e.target.value))}
                                    className="w-28 md:w-44 accent-primary cursor-pointer"
                                />
                            </>
                        )}
                        {activeSliderControl === 'warpScale' && (
                            <>
                                <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-muted whitespace-nowrap min-w-[85px]">
                                    {L.curve}: {(fotoRealTransforms[template]?.warpScale || 0).toFixed(1)}
                                </span>
                                <div className="flex items-center gap-1 border-r border-black/[0.06] dark:border-white/[0.08] pr-2">
                                    <button
                                        onClick={() => setFotoRealTransforms(prev => ({
                                            ...prev,
                                            [template]: { ...prev[template], warpScale: Math.max(-15, parseFloat(((prev[template]?.warpScale || 0) - 0.5).toFixed(1))) }
                                        }))}
                                        className="w-7 h-7 flex items-center justify-center bg-tint-stone/60 hover:bg-tint-stone active:scale-90 text-ink rounded-full transition-all"
                                        title={i18nInstance.t('ui.qc.curveLess')}
                                    >
                                        <Minus size={12} />
                                    </button>
                                    <button
                                        onClick={() => setFotoRealTransforms(prev => ({
                                            ...prev,
                                            [template]: { ...prev[template], warpScale: Math.min(15, parseFloat(((prev[template]?.warpScale || 0) + 0.5).toFixed(1))) }
                                        }))}
                                        className="w-7 h-7 flex items-center justify-center bg-tint-stone/60 hover:bg-tint-stone active:scale-90 text-ink rounded-full transition-all"
                                        title={i18nInstance.t('ui.qc.curveMore')}
                                    >
                                        <Plus size={12} />
                                    </button>
                                </div>
                                <input
                                    type="range"
                                    min={-15}
                                    max={15}
                                    step={0.5}
                                    value={fotoRealTransforms[template]?.warpScale || 0}
                                    onChange={(e) => setFotoRealTransforms(prev => ({
                                        ...prev,
                                        [template]: { ...prev[template], warpScale: parseFloat(e.target.value) }
                                    }))}
                                    className="w-28 md:w-44 accent-primary cursor-pointer"
                                />
                            </>
                        )}
                        {activeSliderControl === 'opacity' && (
                            <>
                                <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-muted whitespace-nowrap min-w-[85px]">
                                    {L.opacity}: {Math.round((fotoRealTransforms[template]?.opacity ?? 0.85) * 100)}%
                                </span>
                                <div className="flex items-center gap-1 border-r border-black/[0.06] dark:border-white/[0.08] pr-2">
                                    <button
                                        onClick={() => setFotoRealTransforms(prev => ({
                                            ...prev,
                                            [template]: { ...prev[template], opacity: Math.max(0.1, parseFloat(((prev[template]?.opacity ?? 0.85) - 0.05).toFixed(2))) }
                                        }))}
                                        className="w-7 h-7 flex items-center justify-center bg-tint-stone/60 hover:bg-tint-stone active:scale-90 text-ink rounded-full transition-all"
                                        title="Diminuir Opacidade 5%"
                                    >
                                        <Minus size={12} />
                                    </button>
                                    <button
                                        onClick={() => setFotoRealTransforms(prev => ({
                                            ...prev,
                                            [template]: { ...prev[template], opacity: Math.min(1.0, parseFloat(((prev[template]?.opacity ?? 0.85) + 0.05).toFixed(2))) }
                                        }))}
                                        className="w-7 h-7 flex items-center justify-center bg-tint-stone/60 hover:bg-tint-stone active:scale-90 text-ink rounded-full transition-all"
                                        title="Aumentar Opacidade 5%"
                                    >
                                        <Plus size={12} />
                                    </button>
                                </div>
                                <input
                                    type="range"
                                    min={0.1}
                                    max={1.0}
                                    step={0.05}
                                    value={fotoRealTransforms[template]?.opacity ?? 0.85}
                                    onChange={(e) => setFotoRealTransforms(prev => ({
                                        ...prev,
                                        [template]: { ...prev[template], opacity: parseFloat(e.target.value) }
                                    }))}
                                    className="w-28 md:w-44 accent-primary cursor-pointer"
                                />
                            </>
                        )}
                        <div className="h-4 w-px bg-black/10 dark:bg-white/15 mx-0.5" />
                        <button
                            onClick={() => setActiveSliderControl(null)}
                            className="w-8 h-8 bg-primary text-slate-900 hover:opacity-90 rounded-full transition-all active:scale-90 flex items-center justify-center"
                            title={i18nInstance.t('ui.qc.doneAdjust')}
                        >
                            <Check size={14} />
                        </button>
                    </div>
                )}

                {/* PREVIEW CONTAINER */}
                <div
                    ref={containerRef}
                    className="flex-1 flex items-center justify-center p-4 md:p-8 relative overflow-hidden"
                >
                    <div
                        className="relative transition-all duration-500 ease-out"
                        style={{
                            width: CARD_W * scale,
                            height: CARD_H * scale,
                            maxWidth: '90vw',
                            maxHeight: '70vh',
                            margin: 'auto',
                            aspectRatio: `${CARD_W} / ${CARD_H}`,
                        }}
                    >
                        <div style={{
                            width: CARD_W,
                            height: CARD_H,
                            transform: `scale(${scale})`,
                            transformOrigin: 'top left',
                            borderRadius: 24,
                            overflow: 'hidden',
                            boxShadow: '0 30px 80px -30px rgba(0,0,0,0.45)',
                        }}>
                            <div
                                ref={captureRef}
                                style={{ width: CARD_W, height: CARD_H, overflow: 'hidden', position: 'relative', clipPath: 'inset(0 0 0 0)' }}
                            >
                                <QuoteCard
                                    data={data}
                                    template={template}
                                    bgColor={bgColor}
                                    fontFamily={fontFamily}
                                    fontSize={fontSize}
                                    isBold={isBold}
                                    isItalic={isItalic}
                                    isUnderline={isUnderline}
                                    textAlign={textAlign}
                                    textColorOverride={textColorOverride}
                                    images={images}
                                    transforms={transforms}
                                    fotoRealTransform={fotoRealTransforms[template]}
                                    isTextSelected={isFotoRealTextSelected && !isExporting}
                                    onSlotClick={handleSlotClick}
                                    onTextClick={() => {
                                        if (template.startsWith('foto-real')) {
                                            setIsFotoRealTextSelected(true);
                                            const currentTf = fotoRealTransforms[template] || getDefaultFotoRealTransform(template);
                                            setFotoRealSnapshot({ ...currentTf });
                                        }
                                    }}
                                />

                                <canvas
                                    ref={canvasRef}
                                    width={CARD_W}
                                    height={CARD_H}
                                    className="absolute inset-0 z-40 pointer-events-none"
                                />
                                {isCreativeMode && (
                                    <div
                                        className="absolute inset-0 z-[45] cursor-crosshair touch-none"
                                        onPointerDown={startDrawing}
                                        onPointerMove={draw}
                                        onPointerUp={endDrawing}
                                        onPointerLeave={endDrawing}
                                    />
                                )}
                                {stampWatermark && (
                                    <div style={{
                                        position: 'absolute', right: 26, bottom: 20, zIndex: 60, pointerEvents: 'none',
                                        fontFamily: "'Geist', 'Inter', system-ui, sans-serif", fontSize: 17, fontWeight: 500,
                                        letterSpacing: '-0.03em', color: '#8a8a8a', opacity: 0.5, lineHeight: 1,
                                    }}>
                                        libritui.
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleFileChange} />

                {/* AÇÕES (Design 2.0) */}
                <div className="px-6 pb-6 pt-2 flex flex-col items-center gap-2.5 z-30">
                    {exportError && <p className="text-[12px] text-bad">{exportError}</p>}
                    <div className="w-full max-w-xs flex gap-2">
                        {canShare && (
                            <button
                                onClick={() => setPendingExport('share')}
                                disabled={isExporting}
                                className="group flex-1 h-12 bg-primary text-slate-900 rounded-full text-[13px] font-medium hover:opacity-90 active:scale-95 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {isExporting ? <Spinner /> : <Share2 size={15} strokeWidth={1.75} className="transition-transform duration-500 group-hover:rotate-[20deg]" />}
                                {L.share}
                            </button>
                        )}
                        <button
                            onClick={() => setPendingExport('download')}
                            disabled={isExporting}
                            title={L.download}
                            className={`group h-12 ${canShare ? 'w-12' : 'flex-1 gap-2 px-5'} shrink-0 flex items-center justify-center rounded-full transition-all duration-300 active:scale-95 disabled:opacity-60 ${canShare ? 'bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone' : 'bg-ink text-surface hover:opacity-90 text-[13px] font-medium'}`}
                        >
                            {isExporting && !canShare ? <Spinner /> : <Download size={16} strokeWidth={1.75} className="transition-transform duration-500 group-hover:translate-y-0.5" />}
                            {!canShare && L.download}
                        </button>
                    </div>
                    <button
                        onClick={handleOpenSocialShare}
                        disabled={isExporting}
                        className="group w-full max-w-xs h-11 rounded-full text-[12px] font-medium text-muted hover:text-ink hover:bg-surface transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                        {isExporting && !isSocialShareOpen ? <Spinner /> : <Sparkles size={13} strokeWidth={1.75} className="transition-transform duration-500 group-hover:rotate-45" />}
                        {L.shareProfile}
                    </button>
                </div>
            </div>

            {/* ── RIGHT SIDEBAR (Foto Real Text Custom Controls) ── */}
            {template.startsWith('foto-real') && (
                <div className={`relative w-28 md:w-36 bg-surface border-l border-black/[0.06] dark:border-white/[0.06] flex flex-col items-center justify-between z-[60] shrink-0 transition-all duration-300 py-3 px-2 select-none overflow-hidden h-full ${isFotoRealTextSelected ? 'translate-x-0 opacity-100 pointer-events-auto visible' : 'translate-x-[120%] opacity-0 pointer-events-none invisible absolute right-0'}`}>
                    
                    <div className="w-full flex flex-col items-center gap-1.5 overflow-y-auto no-scrollbar py-1">
                        <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-muted text-center leading-tight mb-1">{L.editText}</span>

                        {/* 1. Rotação */}
                        <button
                            onClick={() => setActiveSliderControl(activeSliderControl === 'rotation' ? null : 'rotation')}
                            className={`w-full py-2 px-2 rounded-2xl transition-all duration-300 border flex flex-col items-center gap-1 active:scale-95 ${activeSliderControl === 'rotation' ? 'bg-ink border-transparent text-surface' : 'bg-tint-stone/50 border-transparent hover:bg-tint-stone text-ink'}`}
                            title={i18nInstance.t('ui.qc.adjustRotation')}
                        >
                            <RotateCw size={16} />
                            <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em]">{L.rotation}</span>
                        </button>

                        {/* 2. Tamanho */}
                        <button
                            onClick={() => setActiveSliderControl(activeSliderControl === 'width' ? null : 'width')}
                            className={`w-full py-2 px-2 rounded-2xl transition-all duration-300 border flex flex-col items-center gap-1 active:scale-95 ${activeSliderControl === 'width' ? 'bg-ink border-transparent text-surface' : 'bg-tint-stone/50 border-transparent hover:bg-tint-stone text-ink'}`}
                            title={i18nInstance.t('ui.qc.adjustSize')}
                        >
                            <Maximize2 size={16} />
                            <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em]">{L.boxSize}</span>
                        </button>

                        {/* 3. Curvatura */}
                        <button
                            onClick={() => setActiveSliderControl(activeSliderControl === 'warpScale' ? null : 'warpScale')}
                            className={`w-full py-2 px-2 rounded-2xl transition-all duration-300 border flex flex-col items-center gap-1 active:scale-95 ${activeSliderControl === 'warpScale' ? 'bg-ink border-transparent text-surface' : 'bg-tint-stone/50 border-transparent hover:bg-tint-stone text-ink'}`}
                            title={i18nInstance.t('ui.qc.adjustCurve')}
                        >
                            <Sparkles size={16} />
                            <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em]">{L.curve}</span>
                        </button>

                        {/* 4. Opacidade (Novo Tópico) */}
                        <button
                            onClick={() => setActiveSliderControl(activeSliderControl === 'opacity' ? null : 'opacity')}
                            className={`w-full py-2 px-2 rounded-2xl transition-all duration-300 border flex flex-col items-center gap-1 active:scale-95 ${activeSliderControl === 'opacity' ? 'bg-ink border-transparent text-surface' : 'bg-tint-stone/50 border-transparent hover:bg-tint-stone text-ink'}`}
                            title={i18nInstance.t('ui.qc.adjustOpacity')}
                        >
                            <Eye size={16} />
                            <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em]">{L.opacity}</span>
                        </button>

                        {/* 5. Alinhamento */}
                        <div className="flex flex-col items-center gap-1 w-full pt-1">
                            <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em] text-muted text-center">{L.align}</span>
                            <div className="flex gap-1 bg-tint-stone/50 p-1 rounded-full w-full justify-center">
                                {[
                                    { align: 'left' as const, icon: AlignLeft, title: i18nInstance.t('ui.qc.left') },
                                    { align: 'center' as const, icon: AlignCenter, title: i18nInstance.t('ui.qc.center') },
                                    { align: 'right' as const, icon: AlignRight, title: i18nInstance.t('ui.qc.right') },
                                ].map(item => {
                                    const currentAlign = fotoRealTransforms[template]?.textAlign || (template === 'foto-real-2' ? 'center' : 'left');
                                    const isActive = currentAlign === item.align;
                                    const Icon = item.icon;

                                    return (
                                        <button
                                            key={item.align}
                                            onClick={() => setFotoRealTransforms(prev => ({
                                                ...prev,
                                                [template]: { ...prev[template], textAlign: item.align }
                                            }))}
                                            className={`p-1.5 flex-1 rounded-full transition-all flex items-center justify-center ${isActive ? 'bg-ink text-surface' : 'hover:bg-tint-stone text-muted'}`}
                                            title={item.title}
                                        >
                                            <Icon size={14} />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 6. Direção (D-Pad) */}
                        <div className="flex flex-col items-center gap-1 w-full pt-1">
                            <span className="text-[9px] font-normal uppercase font-mono tracking-[0.14em] text-muted text-center">{L.direction}</span>
                            <div className="flex flex-col items-center gap-1 p-1 bg-tint-stone/40 rounded-[22px]">
                                <button
                                    onClick={() => setFotoRealTransforms(prev => ({
                                        ...prev,
                                        [template]: { ...prev[template], top: Math.max(2, (prev[template]?.top || 30) - 1.5) }
                                    }))}
                                    className="p-1.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all flex items-center justify-center"
                                    title={i18nInstance.t('ui.qc.moveUp')}
                                >
                                    <ArrowUp size={14} className="text-ink" />
                                </button>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => setFotoRealTransforms(prev => ({
                                            ...prev,
                                            [template]: { ...prev[template], left: Math.max(2, (prev[template]?.left || 30) - 1.5) }
                                        }))}
                                        className="p-1.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all flex items-center justify-center"
                                        title={i18nInstance.t('ui.qc.moveLeft')}
                                    >
                                        <ArrowLeft size={14} className="text-ink" />
                                    </button>
                                    <button
                                        onClick={() => setFotoRealTransforms(prev => ({
                                            ...prev,
                                            [template]: { ...prev[template], left: Math.min(85, (prev[template]?.left || 30) + 1.5) }
                                        }))}
                                        className="p-1.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all flex items-center justify-center"
                                        title={i18nInstance.t('ui.qc.moveRight')}
                                    >
                                        <ArrowRight size={14} className="text-ink" />
                                    </button>
                                </div>
                                <button
                                    onClick={() => setFotoRealTransforms(prev => ({
                                        ...prev,
                                        [template]: { ...prev[template], top: Math.min(80, (prev[template]?.top || 30) + 1.5) }
                                    }))}
                                    className="p-1.5 bg-tint-stone/60 hover:bg-tint-stone active:scale-90 rounded-full transition-all flex items-center justify-center"
                                    title={i18nInstance.t('ui.qc.moveDown')}
                                >
                                    <ArrowDown size={14} className="text-ink" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions (Redefinir, X, Check) */}
                    <div className="flex flex-col gap-1.5 w-full pt-2 border-t border-black/[0.06] dark:border-white/[0.06] shrink-0">
                        <button
                            onClick={() => {
                                setFotoRealTransforms(prev => ({
                                    ...prev,
                                    [template]: getDefaultFotoRealTransform(template)
                                }));
                                setActiveSliderControl(null);
                            }}
                            className="w-full py-1.5 px-2 bg-tint-stone/50 hover:bg-tint-stone text-muted hover:text-ink rounded-full transition-all text-center flex items-center justify-center gap-1.5 active:scale-95"
                            title={i18nInstance.t('ui.qc.resetAll')}
                        >
                            <RotateCcw size={12} />
                            <span className="text-[9px] font-normal tracking-[0.14em] uppercase font-mono">{L.reset}</span>
                        </button>

                        <button
                            onClick={() => {
                                if (fotoRealSnapshot) {
                                    setFotoRealTransforms(prev => ({
                                        ...prev,
                                        [template]: { ...fotoRealSnapshot }
                                    }));
                                }
                                setIsFotoRealTextSelected(false);
                                setActiveSliderControl(null);
                            }}
                            className="w-full py-2 bg-tint-rose/60 hover:bg-tint-rose text-bad rounded-full transition-all flex items-center justify-center active:scale-95"
                            title={i18nInstance.t('ui.qc.cancelChanges')}
                        >
                            <X size={18} />
                        </button>

                        <button
                            onClick={() => {
                                setIsFotoRealTextSelected(false);
                                setActiveSliderControl(null);
                            }}
                            className="w-full py-2 bg-primary hover:opacity-90 text-slate-900 rounded-full transition-all flex items-center justify-center active:scale-95"
                            title={i18nInstance.t('ui.qc.finishEdit')}
                        >
                            <Check size={18} />
                        </button>
                    </div>
                </div>
            )}

            {/* ── JANELAS DE EDIÇÃO (Design 2.0) ── */}
            {
                activeEditModal && (
                    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-3 sm:p-6 animate-in fade-in duration-300">
                        <div className="absolute inset-0 bg-black/30 backdrop-blur-[20px]" onClick={() => setActiveEditModal(null)} />

                        <div
                            className={`relative w-full ${activeEditModal === 'template' ? 'max-w-2xl' : 'max-w-md'} bg-surface text-ink border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] shadow-[0_32px_80px_-32px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col max-h-[88vh] animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500`}
                        >
                            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                                <h3 className="font-display text-2xl font-light tracking-[-0.04em] text-ink">
                                    {activeEditModal === 'template' && L.chooseStyle}
                                    {activeEditModal === 'color' && L.bgColor}
                                    {activeEditModal === 'text' && L.textStyle}
                                    {activeEditModal === 'textColor' && L.textColor}
                                    {activeEditModal === 'font' && L.fontFamily}
                                    {activeEditModal === 'size' && L.fontSize}
                                    <span className="text-primary">.</span>
                                </h3>
                                <button onClick={() => setActiveEditModal(null)} aria-label={L.cancel} className="group w-9 h-9 rounded-full flex items-center justify-center text-muted hover:text-ink hover:bg-tint-stone transition-colors duration-300">
                                    <X size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:rotate-90" />
                                </button>
                            </div>

                            <div className="px-6 py-4 overflow-y-auto custom-scrollbar flex-1">
                                {activeEditModal === 'template' && (
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 justify-items-center p-1">
                                        {TEMPLATES.map((tpl, i) => (
                                            <div key={tpl.id} className="animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ animationDelay: `${i * 25}ms` }}>
                                                <Thumbnail
                                                    template={tpl.id}
                                                    bgColor={bgColor}
                                                    selected={template === tpl.id}
                                                    onClick={() => {
                                                        setTemplate(tpl.id);
                                                        setImages({});
                                                        setTransforms({});
                                                    }}
                                                    label={tpl.label}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {activeEditModal === 'color' && (
                                    <div className="grid grid-cols-6 gap-3 justify-items-center">
                                        {BG_COLORS.map((c, i) => (
                                            <button
                                                key={c.id}
                                                onClick={() => setBgColorId(c.id)}
                                                title={c.label}
                                                style={{ backgroundColor: c.value, animationDelay: `${i * 30}ms` }}
                                                className={`w-11 h-11 rounded-full transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-110 animate-in fade-in zoom-in-90 ${bgColorId === c.id ? 'ring-2 ring-ink ring-offset-2 ring-offset-surface scale-105' : 'ring-1 ring-black/10 dark:ring-white/10'}`}
                                            />
                                        ))}
                                        <button
                                            onClick={() => setIsCustomColorPickerOpen(true)}
                                            title={L.customColor}
                                            style={{ background: bgColorId === 'custom' ? customBgColor : 'conic-gradient(#e6a1a1,#e8d79c,#a9d6a6,#9fc8de,#b9a8e0,#e6a1a1)' }}
                                            className={`group w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500 hover:scale-110 ${bgColorId === 'custom' ? 'ring-2 ring-ink ring-offset-2 ring-offset-surface' : 'ring-1 ring-black/10 dark:ring-white/10'}`}
                                        >
                                            <Pipette size={15} strokeWidth={1.75} className="text-white drop-shadow transition-transform duration-500 group-hover:-rotate-45" />
                                        </button>
                                    </div>
                                )}

                                {activeEditModal === 'text' && (
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { key: 'bold', label: 'B', active: isBold, toggle: () => setIsBold(v => !v), cls: 'font-bold' },
                                                { key: 'italic', label: 'I', active: isItalic, toggle: () => setIsItalic(v => !v), cls: 'italic font-serif' },
                                                { key: 'underline', label: 'U', active: isUnderline, toggle: () => setIsUnderline(v => !v), cls: 'underline underline-offset-4' },
                                            ].map(btn => (
                                                <button
                                                    key={btn.key} onClick={btn.toggle}
                                                    className={`h-12 rounded-full text-base transition-all duration-300 active:scale-95 ${btn.cls} ${btn.active ? 'bg-ink text-surface' : 'bg-tint-stone/60 text-muted hover:text-ink hover:bg-tint-stone'}`}
                                                >{btn.label}</button>
                                            ))}
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { id: 'left' as const, Icon: AlignLeft },
                                                { id: 'center' as const, Icon: AlignCenter },
                                                { id: 'right' as const, Icon: AlignRight },
                                            ].map(btn => (
                                                <button
                                                    key={btn.id} onClick={() => setTextAlign(btn.id)}
                                                    className={`h-12 rounded-full flex items-center justify-center transition-all duration-300 active:scale-95 ${textAlign === btn.id ? 'bg-ink text-surface' : 'bg-tint-stone/60 text-muted hover:text-ink hover:bg-tint-stone'}`}
                                                ><btn.Icon size={17} strokeWidth={1.75} /></button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {activeEditModal === 'textColor' && (
                                    <div className="grid grid-cols-6 gap-3 justify-items-center">
                                        <button
                                            onClick={() => setTextColorOverride(null)}
                                            title={L.autoColor}
                                            aria-label={L.autoColor}
                                            style={{ background: 'linear-gradient(135deg, #ffffff 50%, #1a1a1a 50%)' }}
                                            className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500 hover:scale-110 ${textColorOverride === null ? 'ring-2 ring-ink ring-offset-2 ring-offset-surface scale-105' : 'ring-1 ring-black/10 dark:ring-white/10'}`}
                                        >
                                            <span className="px-1 rounded bg-surface/85 text-ink text-[11px] font-medium">A</span>
                                        </button>
                                        {TEXT_COLORS.map((c, i) => (
                                            <button
                                                key={c}
                                                onClick={() => setTextColorOverride(c)}
                                                title={c}
                                                aria-label={c}
                                                style={{ backgroundColor: c, animationDelay: `${i * 30}ms` }}
                                                className={`w-11 h-11 rounded-full transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-110 animate-in fade-in zoom-in-90 ${textColorOverride === c ? 'ring-2 ring-ink ring-offset-2 ring-offset-surface scale-105' : 'ring-1 ring-black/10 dark:ring-white/10'}`}
                                            />
                                        ))}
                                        <button
                                            onClick={() => setIsTextColorWheelOpen(true)}
                                            title={L.allColors}
                                            aria-label={L.allColors}
                                            style={{ background: textColorOverride && !TEXT_COLORS.includes(textColorOverride) ? textColorOverride : 'conic-gradient(#ff0000,#ffff00,#00ff00,#00ffff,#0000ff,#ff00ff,#ff0000)' }}
                                            className={`group w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500 hover:scale-110 ${textColorOverride && !TEXT_COLORS.includes(textColorOverride) ? 'ring-2 ring-ink ring-offset-2 ring-offset-surface' : 'ring-1 ring-black/10 dark:ring-white/10'}`}
                                        >
                                            <Pipette size={15} strokeWidth={1.75} className="text-white drop-shadow transition-transform duration-500 group-hover:-rotate-45" />
                                        </button>
                                    </div>
                                )}

                                {activeEditModal === 'font' && (
                                    <div className="grid grid-cols-2 gap-2">
                                        {FONTS.map((f, i) => (
                                            <button
                                                key={f.id} onClick={() => setFontId(f.id)}
                                                style={{ fontFamily: f.family, animationDelay: `${i * 20}ms` }}
                                                className={`h-12 rounded-full text-[14px] transition-all duration-300 active:scale-95 animate-in fade-in slide-in-from-bottom-2 ${fontId === f.id ? 'bg-ink text-surface' : 'bg-tint-stone/60 text-ink/80 hover:text-ink hover:bg-tint-stone'}`}
                                            >{f.label}</button>
                                        ))}
                                    </div>
                                )}

                                {activeEditModal === 'size' && (
                                    <div className="grid grid-cols-4 gap-2">
                                        {FONT_SIZES.map((s, i) => (
                                            <button
                                                key={s.id} onClick={() => setFontSizeId(s.id)}
                                                className={`h-20 rounded-[22px] flex flex-col items-center justify-center gap-1 transition-all duration-300 active:scale-95 ${fontSizeId === s.id ? 'bg-ink text-surface' : 'bg-tint-stone/60 text-muted hover:text-ink hover:bg-tint-stone'}`}
                                            >
                                                <span className="font-serif leading-none" style={{ fontSize: 14 + i * 5 }}>Aa</span>
                                                <span className="font-mono text-[10px] tabular-nums opacity-70">{s.px}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="px-6 pb-6 pt-2">
                                <button
                                    onClick={() => setActiveEditModal(null)}
                                    className="group w-full h-12 bg-primary text-slate-900 rounded-full text-[13px] font-medium flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all duration-300"
                                >
                                    <Check size={15} strokeWidth={1.75} className="transition-transform duration-300 group-hover:scale-110" />
                                    {L.confirm}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Roda de cores do texto */}
            {
                isTextColorWheelOpen && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/30 backdrop-blur-[20px] animate-in fade-in duration-300">
                        <div className="absolute inset-0" onClick={() => setIsTextColorWheelOpen(false)} />
                        <div className="relative bg-surface text-ink border border-black/[0.06] dark:border-white/[0.06] p-6 rounded-[28px] w-full max-w-xs animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500">
                            <div className="flex items-center justify-between mb-5">
                                <h3 className="font-display text-2xl font-light tracking-[-0.04em]">{L.allColors}<span className="text-primary">.</span></h3>
                                <button onClick={() => setIsTextColorWheelOpen(false)} aria-label={L.cancel} className="group w-9 h-9 rounded-full flex items-center justify-center text-muted hover:text-ink hover:bg-tint-stone transition-colors">
                                    <X size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:rotate-90" />
                                </button>
                            </div>
                            <ColorWheel value={textColorOverride || '#ffffff'} onChange={setTextColorOverride} brightnessLabel={L.brightness} />
                            <div className="mt-4 flex items-center gap-2">
                                <span className="w-9 h-9 rounded-full ring-1 ring-black/10 dark:ring-white/10 shrink-0" style={{ backgroundColor: textColorOverride || '#ffffff' }} />
                                <input
                                    type="text"
                                    aria-label={L.hexCode}
                                    defaultValue={textColorOverride || '#ffffff'}
                                    key={textColorOverride || 'auto'}
                                    onChange={(e) => { if (/^#[0-9a-f]{6}$/i.test(e.target.value)) setTextColorOverride(e.target.value.toLowerCase()); }}
                                    className="flex-1 h-9 px-3 rounded-full bg-tint-stone/60 font-mono text-[13px] uppercase outline-none focus:ring-1 focus:ring-ink/20"
                                />
                            </div>
                            <button
                                onClick={() => setIsTextColorWheelOpen(false)}
                                className="mt-5 w-full h-12 bg-primary text-slate-900 rounded-full text-[13px] font-medium flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all duration-300"
                            >
                                <Check size={15} strokeWidth={1.75} />
                                {L.done}
                            </button>
                        </div>
                    </div>
                )
            }
            {/* Cor personalizada */}
            {
                isCustomColorPickerOpen && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-black/30 backdrop-blur-[20px] animate-in fade-in duration-300">
                        <div className="absolute inset-0" onClick={() => setIsCustomColorPickerOpen(false)} />
                        <div className="relative bg-surface text-ink border border-black/[0.06] dark:border-white/[0.06] p-6 rounded-[28px] w-full max-w-xs animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-display text-2xl font-light tracking-[-0.04em]">{L.customColor}<span className="text-primary">.</span></h3>
                                <button onClick={() => setIsCustomColorPickerOpen(false)} className="group w-9 h-9 rounded-full flex items-center justify-center text-muted hover:text-ink hover:bg-tint-stone transition-colors">
                                    <X size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:rotate-90" />
                                </button>
                            </div>

                            <label className="relative mx-auto block w-24 h-24 rounded-full cursor-pointer ring-1 ring-black/10 dark:ring-white/10 transition-transform duration-500 hover:scale-105" style={{ backgroundColor: customBgColor }}>
                                <input
                                    type="color"
                                    value={customBgColor}
                                    onChange={(e) => { setCustomBgColor(e.target.value); setBgColorId('custom'); }}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                            </label>

                            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.14em] text-muted text-center">{L.hexCode}</p>
                            <input
                                type="text"
                                value={customBgColor}
                                onChange={(e) => { setCustomBgColor(e.target.value); setBgColorId('custom'); }}
                                className="mt-2 w-full h-11 bg-tint-stone/60 text-ink px-4 rounded-full font-mono text-sm text-center outline-none border border-transparent focus:border-ink/20 transition-all"
                            />

                            <button
                                onClick={() => setIsCustomColorPickerOpen(false)}
                                className="mt-5 w-full h-12 bg-primary text-slate-900 rounded-full text-[13px] font-medium hover:opacity-90 active:scale-[0.98] transition-all"
                            >
                                {L.confirm}
                            </button>
                        </div>
                    </div>
                )
            }
            {/* Confirmação de compartilhar / baixar (com marca d'água opcional) */}
            {
                pendingExport && (
                    <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 bg-black/30 backdrop-blur-[20px] animate-in fade-in duration-300" onClick={() => setPendingExport(null)}>
                        <div className="relative bg-surface text-ink border border-black/[0.06] dark:border-white/[0.06] p-6 md:p-7 rounded-[28px] w-full max-w-sm animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500" onClick={e => e.stopPropagation()}>
                            <span className="w-12 h-12 rounded-full bg-tint-lavender text-ink flex items-center justify-center">
                                {pendingExport === 'share' ? <Share2 size={18} strokeWidth={1.75} /> : <Download size={18} strokeWidth={1.75} />}
                            </span>
                            <h3 className="mt-5 text-xl font-medium tracking-[-0.02em]">
                                {pendingExport === 'share' ? L.confirmShareTitle : L.confirmDownloadTitle}
                            </h3>
                            <p className="mt-1.5 text-[13px] leading-relaxed text-muted">{L.confirmDesc}</p>

                            <button
                                type="button"
                                role="checkbox"
                                aria-checked={useWatermark}
                                onClick={() => setUseWatermark(v => !v)}
                                className="group mt-5 w-full flex items-center gap-3 p-3 rounded-2xl bg-tint-stone/50 hover:bg-tint-stone transition-colors duration-300 text-left"
                            >
                                <span className={`w-6 h-6 shrink-0 rounded-lg flex items-center justify-center transition-all duration-300 ${useWatermark ? 'bg-ink text-surface' : 'border border-black/20 dark:border-white/25'}`}>
                                    {useWatermark && <Check size={14} strokeWidth={2.25} className="animate-in zoom-in-50 duration-300" />}
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-[13px] font-medium">{L.useWatermark}</span>
                                    <span className="block text-[11px] leading-snug text-muted">{L.watermarkHint}</span>
                                </span>
                                <span className="ml-auto font-sans text-[13px] font-medium tracking-[-0.03em] text-[#8a8a8a] opacity-50">libritui.</span>
                            </button>

                            <div className="mt-6 flex flex-col gap-2">
                                <button
                                    onClick={() => {
                                        const action = pendingExport;
                                        setPendingExport(null);
                                        if (action === 'share') handleShare(useWatermark); else handleSave(useWatermark);
                                    }}
                                    className="w-full h-12 bg-primary text-slate-900 rounded-full text-[13px] font-medium hover:opacity-90 active:scale-[0.98] transition-all duration-300"
                                >
                                    {L.confirm}
                                </button>
                                <button onClick={() => setPendingExport(null)} className="w-full h-11 rounded-full text-muted hover:text-ink text-[13px] font-medium transition-colors duration-300">
                                    {L.cancel}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }            {
                isSocialShareOpen && generatedBlob && (
                    <SocialShareModal
                        data={data}
                        bookId={data.bookId}
                        imageBlob={generatedBlob}
                        onClose={() => setIsSocialShareOpen(false)}
                        onPostSuccess={() => {
                            setIsSocialShareOpen(false);
                            onClose(); // Optional: close the QuoteCardModal entirely after successful post
                        }}
                    />
                )
            }
        </div>
    ), document.body);
};

export default QuoteCardModal;
